/**
 * §9.3 and §9.4 — the journal editor. Coding a transaction, and posting it.
 *
 * This is the screen that reduces the backlog. The triage queue can only look and
 * dismiss; here is where a bank transaction acquires the accounts that say
 * what it was for, and becomes an entry in the books.
 *
 * THE VERSION IS READ, NEVER COMPUTED
 *
 * Every write endpoint requires `expectedVersion`, a trigger owns the
 * increment (migration 0013), and both the save and the post responses carry
 * the new value. So this screen holds the version it last saw and replaces it
 * with whatever the server just returned. It never adds one.
 *
 * That matters most in the save-then-post sequence, which is the normal way to
 * use this screen: saving moves the version, so posting with the version that
 * was read when the page loaded is a stale write and is refused. Getting this
 * wrong produces a screen where saving works, posting works, and doing both in
 * a row does not.
 *
 * A STALE SAVE MUST NOT DISCARD THE OPERATOR'S WORK
 *
 * If a second tab moved the entry on, the server refuses with 409
 * stale_version. The refusal is shown and the form is left exactly as it is.
 * Reloading is the operator's decision, because the alternative — silently
 * replacing what they typed with what the server has — destroys work to fix a
 * conflict they have not seen yet.
 *
 * WHAT THIS SCREEN WILL NOT DECIDE FOR YOU
 *
 * NO ACCOUNT IS EVER PRE-FILLED, and that survived §9.7 arriving.
 *
 * The rules layer (§9.7 step 1) now runs against every unposted entry with a
 * counterparty, and it still does not touch the field. FINDINGS.md is explicit
 * that a suggestion which is right most of the time trains the eye to skip the
 * field, and an operator who has stopped reading a field is worse off than one
 * who had to type it. So the rule is displayed with the pattern that matched
 * (§9.7 step 6) and applied only by a click, onto the first line that has no
 * account -- never over one already chosen.
 *
 * The kNN half of §9.7 is not built and is BLOCKED, not merely unwritten: its
 * query filters on posted headers and step 5 requires tuning against at least
 * 50 of them. Production has 2.
 *
 * A POSTED ENTRY IS EDITED HERE TOO, UNTIL THE BOOKS CLOSE
 *
 * Migration 0027, the operator's decision of 2026-09-13. A posted entry opens
 * editable and saves in place; the only read-only state is an entry dated
 * inside the closed period, which says so and points to Settings. Three
 * things follow on this screen: each line's server id goes back on save, so a
 * line is updated rather than replaced; a match on a line is shown and can be
 * undone where the operator is; and a History panel reads what the triggers
 * recorded. The single-entry post confirmation went, because what it guarded
 * is no longer permanent.
 *
 * The bank-amount reading below is advisory and never blocks posting. §9.3's
 * check is a database trigger and the trigger is the authority; this is a
 * preview so the operator is not sent round a submit-and-be-refused loop. If
 * the two ever disagree, the server is right.
 */

import { RELATIONSHIP_LABELS } from '../components/Form990PfReport.js';
import { keepsNetAssets, projectWord, useBusiness, useCan, useKeepsGoods, useUsesFunds } from '../auth/AuthProvider.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Combobox, type ComboboxOption } from '../components/Combobox.js';
import { LabelChip } from '../components/LabelsSection.js';
import { Toast } from '../components/Toast.js';
import { InfoNote } from '../components/InfoNote.js';
import { GoodsReturnForm } from '../components/GoodsReturnForm.js';
import { describeAttached, ReceiptDropzone } from '../components/ReceiptDropzone.js';
import { ReceiptOverlay } from '../components/ReceiptOverlay.js';
import { describeChange, formatWhen } from '../lib/history.js';
import {
  formatAmountInput,
  formatCents,
  normaliseAmountInput,
  parseCents,
} from '../lib/money.js';
import { kindLabel, roleLabels, statusLabel } from '../lib/labels.js';
import { NewAccountForm } from './AccountsPage.js';
import {
  ApiError,
  api,
  type Attachment,
  type JournalFilters,
  type Counterpart,
  type RuleSuggestion,
  type ResolvedParty,
  type CreateAccountBody,
  type Account,
  type JournalEntry,
  type CreatePartyBody,
  type EntryChange,
  type EntryMatch,
  type Fund,
  type Item,
  type JournalLine,
  type LabelYear,
  type LineLabel,
  type Phase,
  type Project,
  type Party,
  type SaveLinesBody,
} from '../api.js';
import { settingsTab } from '../lib/settings-tabs.js';

interface DraftLine {
  /** Local only: React's row key, which a line needs before it has an id. */
  key: string;
  /**
   * The server's id, for a line the entry already has. Sent back on save.
   *
   * On a draft the server ignores it and replaces every line. On a POSTED
   * entry it is what makes the save update this line in place (migration
   * 0027); without it every line reads as removed and re-added, which is
   * refused for any line that was ever matched to a payment, and which would
   * write a history of removals for an edit that changed one amount.
   */
  id?: string;
  accountId: string;
  /** Raw text, so a half-typed "12." is not destroyed on every keystroke. */
  debit: string;
  credit: string;
  /**
   * The line's fund (migration 0079), in a business that keeps funds. Empty
   * means the default fund, General, which is where the server puts a new line
   * sent without one; the picker shows it chosen.
   */
  fundId: string;
  /**
   * The line's project, a non-profit's program (migration 0081). Empty is
   * "not chosen": the server keeps a stored line's own and puts a non-profit's
   * new expense line in its default program. Null is "none", chosen.
   */
  projectId: string | null;
  /**
   * The line's phase (migration 0091). Empty is "not chosen": the server keeps
   * a stored line's own and gives a new line none. Null is "none", chosen.
   */
  phaseId: string | null;
  /**
   * Migration 0086: the item a stock line moves, or a sale's revenue line
   * sells, and how many of its unit, as typed. Empty is none.
   */
  itemId: string;
  quantity: string;
  /** Migration 0086: on a cost line, the key of the stock line whose cost it is (§14.3). */
  costsKey: string | null;
  /** Migration 0086: a stored line a draw names, frozen once posted (round 131's IB-3). */
  drawn: boolean;
  /** Migration 0088 (§15.3): on a transfer's stock debit, the key of the stock credit whose goods it moves. */
  fromKey: string | null;
  /**
   * Migration 0090: the labels the line carries. Sent when there are any, or
   * the stored line had some; a label of another year is carried to its
   * rolled-over counterpart when the entry's date moves (§D.5.2).
   */
  labelIds: string[];
}

let nextKey = 0;
const newLine = (): DraftLine => ({
  key: `line-${(nextKey += 1)}`,
  accountId: '',
  debit: '',
  credit: '',
  fundId: '',
  projectId: '',
  phaseId: '',
  itemId: '',
  quantity: '',
  costsKey: null,
  drawn: false,
  fromKey: null,
  labelIds: [],
});

/** "12.5000" as typed: "12.5". */
const typedQuantity = (quantity: string | null | undefined): string =>
  quantity == null ? '' : quantity.includes('.') ? quantity.replace(/\.?0+$/, '') : quantity;

/** A stored line as the form holds it, id included. */
const fromStored = (line: JournalLine): DraftLine => ({
  key: `line-${(nextKey += 1)}`,
  id: line.id,
  accountId: line.accountId,
  debit: line.debitCents > 0 ? formatAmountInput(line.debitCents) : '',
  credit: line.creditCents > 0 ? formatAmountInput(line.creditCents) : '',
  fundId: line.fundId ?? '',
  projectId: line.projectId ?? '',
  phaseId: line.phaseId ?? '',
  itemId: line.itemId ?? '',
  quantity: typedQuantity(line.quantity),
  costsKey: null,
  drawn: line.drawn === true,
  fromKey: null,
  labelIds: (line.labels ?? []).map((label) => label.id),
});

/**
 * Stored lines as the form holds them, each cost line naming its stock line by
 * the form's key (0086), and a transfer's stock debit the stock credit on the
 * same entry whose goods it moves (0088). A return's stock debit names a line
 * of another entry, which the form does not hold.
 */
const storedLines = (lines: JournalLine[]): DraftLine[] => {
  const drafts = lines.map(fromStored);
  const keyOf = new Map(drafts.map((draft) => [draft.id, draft.key]));
  return drafts.map((draft, index) => {
    const costs = lines[index]!.costsDetailId;
    const from = lines[index]!.fromDetailId;
    return {
      ...draft,
      ...(costs && { costsKey: keyOf.get(costs) ?? null }),
      ...(from && { fromKey: keyOf.get(from) ?? null }),
    };
  });
};

/**
 * 0086: what the save sends for a line's goods. An item and quantity where the
 * line has an item; on a cost line, the place in the saved lines of the stock
 * line whose cost it is. Nothing at all on any other line, so a posted line
 * sent back keeps what it has.
 */
const goodsFields = (line: DraftLine, saved: DraftLine[]) => ({
  ...(line.itemId !== '' && { itemId: line.itemId, quantity: line.quantity.trim() === '' ? null : line.quantity.trim() }),
  ...(line.costsKey !== null &&
    saved.some((candidate) => candidate.key === line.costsKey) && {
      costsLine: saved.findIndex((candidate) => candidate.key === line.costsKey),
    }),
  // 0088 (§15.3): a transfer's stock debit, the place of the stock credit whose goods it moves.
  ...(line.fromKey !== null &&
    saved.some((candidate) => candidate.key === line.fromKey) && {
      fromLine: saved.findIndex((candidate) => candidate.key === line.fromKey),
    }),
});

/** What the New Entry menu's goods entries open as (0086, §14.7; 0088's Move Stock, §15.3). */
type GoodsKind = 'receive' | 'giveaway' | 'shrinkage' | 'transfer';

/** A goods entry's heading from the New Entry menu, as the menu names it. */
const GOODS_TITLES: Record<GoodsKind, string> = {
  receive: 'Receive Goods',
  giveaway: 'Give Goods Away',
  shrinkage: 'Record Shrinkage',
  transfer: 'Move Stock',
};

/**
 * Round 130's observation: a stored goods entry has no bank descriptor and
 * often no party, so it is headed by what its stock lines do rather than
 * read as an uncategorized bank row.
 */
function goodsTitle(lines: ReadonlyArray<{ id: string; holdsStock?: boolean; creditCents: number; fromDetailId?: string | null }>): string | null {
  const stock = lines.filter((line) => line.holdsStock === true);
  if (stock.length === 0) return null;
  // 0088: a transfer's stock debit names a line of its own entry; a return's, the sale's cost entry.
  const from = stock.map((line) => line.fromDetailId).filter((id): id is string => typeof id === 'string');
  if (from.some((id) => lines.some((line) => line.id === id))) return 'Stock Moved';
  if (from.length > 0) return 'Goods Returned';
  return stock.some((line) => line.creditCents > 0) ? 'Goods Out Of Stock' : 'Goods Into Stock';
}

/**
 * 0086: a goods entry's opening lines. Receiving is a stock debit beside a
 * blank line for what paid for it, or gave it. Giving away and shrinkage are
 * an expense line that is the cost of a stock credit; the stock line's amount
 * is the preview's, priced again when the entry posts. Debits lead.
 */
function goodsEntryLines(goods: GoodsKind, accounts: Account[]): DraftLine[] {
  const stock = accounts.find((account) => account.inventoryRole === 'stock' && account.isActive);
  const stockLine: DraftLine = { ...newLine(), accountId: stock?.id ?? '', quantity: '1' };
  if (goods === 'receive') return [stockLine, newLine()];
  if (goods === 'transfer') {
    // 0088 (§15.3): out of one pool and into another, the second naming the first; a second stock account if there is one.
    const other = accounts.filter((account) => account.inventoryRole === 'stock' && account.isActive)[1] ?? stock;
    return [stockLine, { ...newLine(), accountId: other?.id ?? '', quantity: '1', fromKey: stockLine.key }];
  }
  const expenseCode = goods === 'giveaway' ? '6860' : '6870';
  const expense = accounts.find((account) => account.code === expenseCode && account.isActive);
  const costLine: DraftLine = { ...newLine(), accountId: expense?.id ?? '', costsKey: stockLine.key };
  return [costLine, { ...stockLine, credit: '' }];
}

/**
 * 0079: the lines a save wrote between funds are the save's, never the form's.
 * They are shown beneath the lines, read-only, and never sent back.
 */
const editable = (lines: JournalLine[]): JournalLine[] => lines.filter((line) => line.interfund !== true);

/**
 * The header a NEW entry pretends to have until it is saved.
 *
 * A synthetic object rather than null checks scattered through the render. The
 * screen asks `entry` a great many questions -- is it posted, is it manual,
 * what did the bank report -- and every one of them has a correct answer for
 * an entry that does not exist yet: it is a manual draft with nothing in it.
 *
 * The one field this does NOT own is the date. That is editable here, so it
 * lives in its own state and this carries a placeholder.
 */
function blankManualEntry(): JournalEntry {
  return {
    id: 'new',
    entryDate: '',
    cashDate: null,
    status: 'draft',
    source: 'manual',
    counterparty: null,
    memo: null,
    invoiceReference: null,
    dueDate: null,
    bankAmountCents: null,
    mercuryCategory: null,
    mercuryKind: null,
    version: 0,
    partyId: null,
    partyName: null,
    debitTotalCents: 0,
    creditTotalCents: 0,
    balanced: false,
    mercuryTransactionId: null,
    mercuryAccountId: null,
    bankAccountId: null,
    // A manual entry has no bank text, and 0068's CHECK says so at the
    // database: source IN ('mercury', 'bank_feed') OR bank_memo IS NULL.
    bankMemo: null,
    postedAt: null,
    postedBy: null,
    dismissedAt: null,
    reversesHeaderId: null,
    reversedByHeaderId: null,
    relatedHeaderId: null,
    relationKind: null,
    closesFiscalYear: null,
    lines: [],
  };
}

/** Today, as the calendar date the operator is looking at. §5.4. */
function todayISO(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * §17 — the lines a NEW entry opens with, by what is being recorded.
 *
 * An invoice always debits the receivables control account. That is not a
 * guess: `account_role = 'ar_control'` is what migration 0016's view and the
 * whole matching screen find receivables by. A non-profit's chart has two,
 * 1200 and 1250 Pledges Receivable (0084), so an invoice takes the first that
 * is not a pledge account, and a PLEDGE takes the first that is, credited to
 * the first cash contribution account (§B.17.4). The account is determined and
 * only the revenue side is a decision -- the same shape as the derived bank
 * line on a Mercury entry.
 *
 * THE AMOUNT STAYS BLANK. Nothing here knows it, and the fill affordance
 * offers the other side once either one is typed.
 *
 * Debits lead, so receivables is row one.
 *
 * A general journal entry gets two blank lines, because nothing about it is
 * determined.
 */
function newEntryLines(kind: EntryKind, accounts: Account[]): DraftLine[] {
  if (kind === 'journal') return [newLine(), newLine()];

  const pledge = kind === 'pledge';
  const receivable = accounts.find(
    (account) =>
      account.accountRole === 'ar_control' && account.isActive && (account.contributionKind === 'pledge') === pledge,
  );
  if (!receivable) return [newLine(), newLine()];
  const line = (accountId: string): DraftLine => ({
    key: `line-${(nextKey += 1)}`, accountId, debit: '', credit: '', fundId: '', projectId: '', phaseId: '', itemId: '', quantity: '', costsKey: null, drawn: false, fromKey: null, labelIds: [],
  });
  const contribution = pledge
    ? accounts.find((account) => account.contributionKind === 'cash' && account.isActive)
    : undefined;
  return [line(receivable.id), contribution ? line(contribution.id) : newLine()];
}

/** What a new entry is: the New Entry menu's choice, kept in the address (issue #65). */
type EntryKind = 'journal' | 'invoice' | 'pledge';

/**
 * §10 — the queue's five views, as the filters the list endpoint takes.
 *
 * DUPLICATED FROM TriagePage ON PURPOSE, and the duplication is narrow: that
 * file's VIEWS also carries the tab LABELS, which are the operator's
 * vocabulary and belong to the screen that shows tabs. What is shared is the
 * filter shape, and it is four short objects.
 *
 * If a sixth view is ever added, add it in both places. A mismatch shows up
 * immediately -- Previous and Next would walk a different list from the tab
 * the operator came from -- which is the failure being visible rather than
 * silent.
 */
const QUEUE_VIEWS: Record<string, JournalFilters> = {
  uncategorized: { status: 'draft', uncoded: 'true' },
  draft: { status: 'draft', uncoded: 'false' },
  posted: { status: 'posted' },
  excluded: { status: 'dismissed' },
  all: {},
};

/**
 * §9.3 — the lines an UNCODED entry opens with.
 *
 * THE BANK SIDE IS DERIVED, NOT SUGGESTED, AND THAT DISTINCTION IS THE WHOLE
 * JUSTIFICATION FOR FILLING IT IN.
 *
 * A matched rule fills an uncoded entry in too, since 2026-09-12. This is
 * still a different kind of thing, and the distinction is worth keeping even
 * now that both fill: a rule is a standing instruction that could be wrong
 * about this transaction, while none of the bank side is a guess at all.
 *
 *   - WHICH account. `accounts.mercury_account_id` maps the Mercury account the
 *     money actually moved on to a ledger account. Migration 0014's trigger
 *     resolves it the same way and REFUSES to post an entry whose lines on that
 *     account do not net to the bank amount. Production has 164 headers on 1000
 *     and 150 on 2100; every one of them must carry that account's line.
 *   - HOW MUCH. `bank_amount_cents`, as the bank reported it.
 *   - WHICH SIDE. The sign. Negative is money leaving, so the bank account is
 *     credited. Same convention `fillFor` already uses below.
 *
 * So this is not the engine having an opinion. It is the entry's other half,
 * already known, already enforced, and previously typed by hand on every one of
 * the bank-fed headers in production.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 *
 * It only ever runs on an entry with NO lines. An entry that has been coded,
 * even partly, is left exactly as saved -- the rule everywhere else on this
 * screen: never change an answer the operator already gave.
 *
 * It writes nothing. The lines are local until the entry is saved, like the
 * party fill, and the operator can change the account, move the amount or
 * delete the line outright. A netted bank fee (§6.5) and a multi-way split are
 * both legitimate and both start from exactly this shape.
 */
/**
 * The chart account a bank transaction moved money on. A Mercury row is
 * resolved through accounts.mercury_account_id, exactly as migration 0014's
 * trigger does; an imported file's row names its account (0067). Not "the
 * bank account" -- there are several, and both the operating account and the
 * credit card carry account_role 'bank', so picking by role would net two
 * accounts together.
 */
function bankAccountOf(entry: JournalEntry, accounts: Account[]): Account | undefined {
  if (entry.source === 'mercury') {
    return accounts.find(
      (account) => entry.mercuryAccountId !== null && account.mercuryAccountId === entry.mercuryAccountId,
    );
  }
  if (entry.source === 'bank_feed') {
    return accounts.find((account) => account.id === entry.bankAccountId);
  }
  return undefined;
}

function openingLines(entry: JournalEntry, accounts: Account[]): DraftLine[] {
  const blank = [newLine(), newLine()];
  if (entry.source === 'manual' || entry.bankAmountCents === null) return blank;

  const found = bankAccountOf(entry, accounts);
  const mapped = found?.isActive ? found : undefined;
  // No mapping is a real state, not an error: a Mercury account nobody has
  // mapped yet. Two blank lines, exactly as before.
  if (!mapped) return blank;

  const amount = formatAmountInput(Math.abs(entry.bankAmountCents));
  const outgoing = entry.bankAmountCents < 0;

  const bankLine: DraftLine = {
    key: `line-${(nextKey += 1)}`,
    accountId: mapped.id,
    debit: outgoing ? '' : amount,
    credit: outgoing ? amount : '',
    fundId: '',
    projectId: '',
    phaseId: '',
    itemId: '',
    quantity: '',
    costsKey: null,
    drawn: false,
    fromKey: null,
    labelIds: [],
  };

  /*
   * DEBITS FIRST. It is how a journal entry is written, and the operator reads
   * these as journal entries.
   *
   * So the order follows the SIDE, not the derivation. Money out credits the
   * bank, so the blank categorisation line leads and the bank line follows.
   * Money in debits the bank, so the bank line leads.
   *
   * THIS COSTS SOMETHING AND THE TRADE WAS MADE DELIBERATELY. An earlier
   * version always put the derived line second, reasoning that line 1 is where
   * attention starts and it should be the field the operator has to think
   * about. On a money-in entry that now puts the already-answered field first.
   * Convention won: a reader scanning a column of entries expects debits above
   * credits, and an entry that breaks it reads as an error even when correct.
   *
   * It also settles the ordering when a RULE fills the blank line in. The rule
   * lands on the first line with no account, which is the correct side by
   * construction here, so nothing has to be re-sorted afterwards -- and
   * re-sorting rows under a cursor mid-edit is a thing to avoid, not to build.
   */
  return outgoing ? [newLine(), bankLine] : [bankLine, newLine()];
}

export function EditorPage() {
  // What this screen offers (0069); the API refuses the rest either way.
  const mayEdit = useCan()('edit_entries');
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  /*
   * §10 — walking the queue without leaving the editor.
   *
   * THE LIST IS A SNAPSHOT, TAKEN ONCE, AND THAT IS DELIBERATE. Coding an
   * entry usually removes it from the view it was in -- an Uncategorized draft
   * becomes a Draft the moment it has a line -- so a list refetched after each
   * save would renumber itself under the operator and "next" would skip
   * entries. Walking the queue as it was when they opened it is what they
   * mean by next.
   *
   * The filter travels in the URL from the queue, so this reproduces the list
   * they were actually looking at rather than a default one.
   *
   * WHAT IT DOES NOT DO: paging. The window is the first 100 rows of that
   * view, which is the API's maximum for one request. An entry outside it gets
   * no Previous or Next rather than a wrong one. Production has enough
   * uncategorized drafts to exceed that in one sitting, so this is a real
   * boundary and not a theoretical one -- go back to the queue and pick up.
   */
  const [neighbours, setNeighbours] = useState<string[]>([]);

  /** §9.1 — the other Mercury view of this movement, if there is one. */
  const [counterpart, setCounterpart] = useState<Counterpart | null>(null);

  /*
   * §17 — an entry that does not exist yet.
   *
   * `/journals/new` opens this screen with nothing written. The header is
   * created by the first save, together with its lines, so abandoning a new
   * entry costs nothing -- which the old four-field form achieved only by
   * refusing to create the row until you submitted it.
   *
   * The four fields that form collected live here instead, because they are
   * exactly the fields this screen could not edit. That, and not any view
   * about invoices, is why the form existed.
   */
  const isNew = id === 'new';
  const kind: EntryKind = (['invoice', 'pledge'] as const).find((value) => value === searchParams.get('kind')) ?? 'journal';
  /** 0086: a goods entry from the New Entry menu, which decides only the opening lines. */
  const goodsKind: GoodsKind | null =
    (['receive', 'giveaway', 'shrinkage', 'transfer'] as const).find((value) => value === searchParams.get('kind')) ?? null;
  /** An invoice or a pledge (0084): a receivable, with a number and terms. */
  const receivableKind = kind !== 'journal';
  /** Issue #65: the address a new entry's first save moves to keeps its kind. */
  const kindSuffix = receivableKind ? `?kind=${kind}` : '';
  /** Issue #65: the form's lines, carried across a receipt's save-first to the draft it made. */
  const carriedLines = useRef<DraftLine[] | null>(null);

  const [entryDate, setEntryDate] = useState('');
  const [invoiceReference, setInvoiceReference] = useState('');
  /** Migration 0036. '' means no terms recorded, which is not "due today". */
  const [dueDate, setDueDate] = useState('');
  /** Migration 0084: what a donor received in return for an event gift, as typed; '' is nothing. */
  const [goodsServices, setGoodsServices] = useState('');

  const [entry, setEntry] = useState<JournalEntry | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [version, setVersion] = useState<number | null>(null);

  const [lines, setLines] = useState<DraftLine[]>([]);
  /** 0079: whether lines name funds, the business's funds, and the save's lines between them. */
  const usesFunds = useUsesFunds();
  const [funds, setFunds] = useState<Fund[]>([]);
  const [interfundLines, setInterfundLines] = useState<JournalLine[]>([]);
  /** 0081: the business's projects (a non-profit's programs), and what it calls them. */
  const business = useBusiness();
  const nonprofit = keepsNetAssets(business);
  const [projects, setProjects] = useState<Project[]>([]);
  /** 0086: the business's goods, read only where it keeps any. */
  const keepsGoods = useKeepsGoods();
  const [items, setItems] = useState<Item[]>([]);
  /** 0086: the preview's refusal for a stock line, by the line's key (§14.7). */
  const [goodsErrors, setGoodsErrors] = useState<Record<string, string>>({});
  const [memo, setMemo] = useState('');
  const [cashDate, setCashDate] = useState('');
  const [parties, setParties] = useState<Party[]>([]);
  const [partyId, setPartyId] = useState<string>('');
  /** How the party got filled in, when it was filled rather than chosen. §7. */
  const [partyMatch, setPartyMatch] = useState<ResolvedParty | null>(null);
  /** An alias the operator has been offered, or is saving. */
  const [aliasOffer, setAliasOffer] = useState<string | null>(null);
  /**
   * The alias just created, so the confirmation can offer to undo it.
   *
   * An id rather than a boolean: "it was saved" is not enough to take it back,
   * and the operator's chance to notice a mistaken alias is the moment they
   * make it -- not a trip to the parties screen later.
   */
  const [savedAlias, setSavedAlias] = useState<{ id: string; alias: string } | null>(null);

  /** §9.7 step 1 — the rule that matches this counterparty, if one does. */
  const [ruleSuggestion, setRuleSuggestion] = useState<RuleSuggestion | null>(null);
  /** The rule just created. Same reasoning as the alias above. */
  const [savedRule, setSavedRule] = useState<{ id: string; pattern: string } | null>(null);
  /**
   * The mapping already existed, so the offer is withdrawn without anything
   * having been created here. Separate from `saved*`: there is nothing to undo.
   */
  const [aliasDone, setAliasDone] = useState(false);
  const [ruleDone, setRuleDone] = useState(false);
  /** The §9.3 explanation is collapsed by default -- see the rail below. */
  const [bankNoteOpen, setBankNoteOpen] = useState(false);
  /** §7 — the party-provenance sentence, behind the same kind of control. */
  const [partyNoteOpen, setPartyNoteOpen] = useState(false);
  /**
   * The account being created inline: which line asked for it, and the name
   * typed into the picker.
   *
   * The line key is carried so the new account can be SELECTED on the row that
   * asked. Creating an account and then having to find it in the dropdown you
   * just created it from is the kind of half-finished flow that makes people
   * stop using the inline path.
   */
  const [draftAccount, setDraftAccount] = useState<{ lineKey: string; name: string } | null>(
    null,
  );
  /** The party being created inline, or null when the form is closed. */
  const [draftParty, setDraftParty] = useState<CreatePartyBody | null>(null);
  const [receipts, setReceipts] = useState<Attachment[]>([]);

  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * §6.9 — the books' lock date, read with the entry.
   *
   * Since migration 0027 it is the ONLY thing that makes a posted entry fixed,
   * and the entry read does not carry it, so the screen asks. A failed read is
   * treated as open: the database refuses a change in a closed period whatever
   * this screen believes, and the refusal arrives as a sentence.
   */
  const [lockedThrough, setLockedThrough] = useState<string | null>(null);

  /**
   * §6.3 — payment matches on this entry's lines, live only.
   *
   * Filled on load for a posted entry, and replaced by `detail.matches` when a
   * save or a delete is refused because of them. One list for both, because
   * they are the same fact asked at two moments.
   */
  const [matches, setMatches] = useState<EntryMatch[]>([]);
  /** The match being undone, and the reason typed so far. */
  const [undoing, setUndoing] = useState<{ clearingId: string; reason: string } | null>(null);

  /** §5.7 — the History panel. Null until it is first opened. */
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<EntryChange[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);

  /** Every account, inactive ones too, so History can name what it records. */
  const [allAccounts, setAllAccounts] = useState<Account[]>([]);

  /** The delete confirmation is showing. */
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  /** The reversal form: null when closed, the reason being typed when open. */
  const [reversing, setReversing] = useState<string | null>(null);

  /*
   * A toast that has to survive the navigation that caused it.
   *
   * Saving a NEW entry replaces the route with the id it just created, which
   * re-runs the load effect, which clears every per-entry piece of state --
   * including the "Saved" toast, milliseconds after it was set. A ref carries
   * it across, and the effect hands it back after the reset.
   */
  const pendingNotice = useRef<string | null>(null);
  /** The same, for a refusal: a new entry saved but refused posting. */
  const pendingError = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    /*
     * EVERYTHING ABOUT THE PREVIOUS ENTRY GOES, FIRST.
     *
     * Previous and Next change the route PARAM, not the route, so React keeps
     * this component mounted and every piece of state with it. The visible
     * symptom was a "Saved" toast still sitting there on the next entry. The
     * unseen ones were worse and silent: the last entry's rule suggestion, its
     * alias offer, its transfer counterpart, a half-open create-a-party form.
     * All of them would have read as belonging to the entry now on screen.
     *
     * WHY NOT `key={id}` ON THE ROUTE, which would remount and reset all of
     * this for free: the neighbour snapshot is the one thing that must NOT
     * reset. It is taken once so that coding an entry -- which usually removes
     * it from the view it was in -- cannot renumber the list underneath the
     * operator mid-walk. Remounting would refetch it on every step and Next
     * would start skipping entries.
     *
     * So the reset is explicit and lives in one place. Anything added to this
     * component that belongs to ONE entry belongs in here too.
     */
    setEntry(null);
    setVersion(null);
    setLines([]);
    setMemo('');
    setCashDate('');
    setPartyId('');
    setEntryDate('');
    setInvoiceReference('');
    setGoodsServices('');
    setReversing(null);
    setReceipts([]);
    setPartyMatch(null);
    setAliasOffer(null);
    setSavedAlias(null);
    setAliasDone(false);
    setRuleSuggestion(null);
    setSavedRule(null);
    setRuleDone(false);
    setCounterpart(null);
    setBankNoteOpen(false);
    setPartyNoteOpen(false);
    setDraftAccount(null);
    setDraftParty(null);
    setLockedThrough(null);
    setMatches([]);
    setUndoing(null);
    setHistoryOpen(false);
    setHistory(null);
    setHistoryError(null);
    setConfirmingDelete(false);
    setBusy(false);
    setLoadError(null);
    // Anything the navigation itself wanted to say survives the reset.
    setSaveError(pendingError.current);
    pendingError.current = null;
    setNotice(pendingNotice.current);
    pendingNotice.current = null;

    /*
     * A new entry fetches no header, no attachments and no version. There is
     * nothing to fetch: the row does not exist. Accounts and parties are still
     * needed, because coding it is the entire point.
     */
    if (isNew) {
      Promise.all([api.listAccounts(), api.listParties()])
        .then(([accountResult, partyResult]) => {
          if (cancelled) return;
          setAccounts(accountResult.accounts.filter((account) => account.isActive));
          setParties(partyResult.parties);
          setEntry(blankManualEntry());
          setLines(goodsKind ? goodsEntryLines(goodsKind, accountResult.accounts) : newEntryLines(kind, accountResult.accounts));
          setEntryDate(todayISO());
        })
        .catch((caught: unknown) => {
          if (cancelled) return;
          setLoadError(
            caught instanceof ApiError ? caught.message : 'Could not open a new entry.',
          );
        });
      return () => {
        cancelled = true;
      };
    }

    Promise.all([
      api.getJournal(id),
      api.listAccounts(),
      api.listParties(),
      api.listAttachments(id),
      // Never blocks the entry from opening. See `lockedThrough`.
      api.getBooksLock().catch(() => null),
    ])
      .then(([entryResult, accountResult, partyResult, attachmentResult, lockResult]) => {
        if (cancelled) return;
        const loaded = entryResult.entry;
        const lock = lockResult?.lockedThrough ?? null;
        const loadedLocked =
          loaded.status === 'posted' && lock !== null && loaded.entryDate <= lock;
        setEntry(loaded);
        setVersion(loaded.version);
        setLockedThrough(lock);
        setAccounts(accountResult.accounts.filter((account) => account.isActive));
        setAllAccounts(accountResult.accounts);
        setParties(partyResult.parties);
        setReceipts(attachmentResult.attachments);
        setPartyId(loaded.partyId ?? '');
        setInvoiceReference(loaded.invoiceReference ?? '');
        setDueDate(loaded.dueDate ?? '');
        setGoodsServices(
          loaded.goodsServicesCents == null ? '' : (loaded.goodsServicesCents / 100).toFixed(2),
        );

        /*
         * §6.3 — the matches that would stop a line changing, shown before
         * any save rather than discovered by one. Only a posted entry can be
         * matched, so a draft does not ask.
         */
        if (loaded.status === 'posted') {
          api
            .getJournalMatches(loaded.id)
            .then((result) => {
              if (!cancelled) setMatches(result.matches);
            })
            // No list is the same state as a failed lookup. A save that meets
            // a match is refused with the list attached anyway.
            .catch(() => {});
        }

        /*
         * §7 — fill the party from the counterparty, when there is not one.
         *
         * Only when `party_id` is null. An entry the operator has already
         * classified is never overwritten. An entry in a closed period is not
         * filled either: nothing on it can be saved, so a filled field would
         * be an offer that cannot be taken.
         *
         * The fill is LOCAL until saved. Nothing writes party_id behind the
         * operator's back: §9.7's standing rule is that suggestions are never
         * auto-applied, and an alias is a stronger claim than a suggestion
         * without being strong enough to rewrite stored data unseen. Saving is
         * what commits it, and the rail below says where it came from.
         */
        if (loaded.partyId === null && loaded.counterparty !== null && !loadedLocked) {
          api
            .resolveParty(loaded.counterparty)
            .then((match) => {
              if (cancelled || match.party === null) return;
              setPartyId(match.party.partyId);
              setPartyMatch(match.party);
            })
            // A failed lookup leaves the field empty, which is where it started.
            // It must never block the editor from opening.
            .catch(() => {});
        }

        /*
         * §9.7 step 1 — ask whether a rule codes this counterparty.
         *
         * Asked for EVERY unposted entry with a counterparty, including one
         * that already has lines: a rule is also worth showing beside a coding
         * the operator is revising. Nothing is filled in from the answer --
         * see the rail below.
         */
        /*
         * §9.1 — is this one movement reported twice?
         *
         * Asked for any unposted bank-fed entry. Nothing is excluded from the
         * answer: it is inferred from date, amount and account, which is strong
         * evidence and not proof, and a wrong automatic dismissal would hide a
         * real transaction with nothing anywhere to flag it.
         */
        if (loaded.status !== 'posted' && loaded.source !== 'manual') {
          api
            .findCounterpart(loaded.id)
            .then((match) => {
              if (!cancelled) setCounterpart(match?.counterpart ?? null);
            })
            // A failed lookup shows nothing. It must never block coding.
            .catch(() => {});
        }

        if (loaded.status !== 'posted' && loaded.counterparty !== null) {
          api
            .matchCodingRule(loaded.counterparty)
            .then((match) => {
              if (!cancelled) setRuleSuggestion(match?.suggestion ?? null);
            })
            // Same reasoning as the party lookup. A suggestion that cannot be
            // fetched is a suggestion that is not shown, never a blocked edit.
            .catch(() => {});
        }
        setMemo(loaded.memo ?? '');
        setCashDate(loaded.cashDate ?? '');
        setInterfundLines(loaded.lines.filter((line) => line.interfund === true));
        const carried = carriedLines.current;
        carriedLines.current = null;
        if (carried !== null && loaded.status === 'draft') {
          // Issue #65: the form as the operator left it, including a line still
          // waiting for its amount. A draft's lines are replaced on its next save.
          setLines(carried.map((line) => ({ ...line, id: undefined })));
          return;
        }
        setLines(
          loaded.lines.length > 0
            ? storedLines(editable(loaded.lines))
            : // An uncoded entry opens with two blank lines, because the
              // smallest possible entry has two and starting from zero makes
              // the first action "add a line" rather than "code it".
              openingLines(loaded, accountResult.accounts),
        );
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setLoadError(caught instanceof ApiError ? caught.message : 'Could not load the entry.');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  /*
   * The queue's own query, carried to the neighbours. `kind` is this entry's
   * alone (issue #65 keeps it on a new invoice's address), and carried on it
   * would make a neighbouring manual draft read as an invoice (review IN-1).
   */
  const queueQuery = (() => {
    const query = new URLSearchParams(searchParams);
    query.delete('kind');
    return query.toString();
  })();

  useEffect(() => {
    let cancelled = false;
    const view = searchParams.get('view') ?? 'uncategorized';
    const q = searchParams.get('q') ?? undefined;
    /*
     * Advanced search travels too, or Next would walk a wider list than the
     * one the operator filtered.
     *
     * Read as three-valued, matching the queue's boxes: 'false' is a filter
     * and not an absent one. Testing for === 'true' dropped it, so a queue
     * filtered to "no receipt" walked into an editor stepping through every
     * entry -- the filter silently stopped applying at the moment the operator
     * left the list it was applied to.
     */
    const autoFilled = searchParams.get('autoFilled');
    const hasReceipt = searchParams.get('hasReceipt');
    const filters = QUEUE_VIEWS[view] ?? QUEUE_VIEWS.uncategorized!;

    api
      .listJournals({
        ...filters,
        ...(q ? { q } : {}),
        ...((autoFilled === 'true' || autoFilled === 'false') && { autoFilled }),
        ...((hasReceipt === 'true' || hasReceipt === 'false') && { hasReceipt }),
        limit: 100,
        offset: 0,
      })
      .then((result) => {
        if (!cancelled) setNeighbours(result.entries.map((row) => row.id));
      })
      // No neighbours is the same state as a failed lookup: the buttons are
      // absent. It must never stop the entry itself from being coded.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [queueQuery]);

  const position = neighbours.indexOf(id);
  const previousId = position > 0 ? neighbours[position - 1] : undefined;
  const nextId = position >= 0 ? neighbours[position + 1] : undefined;

  /*
   * §10 — Previous and Next from the keyboard.
   *
   * `[` and `]`, matching the queue's `j`/`k` model: single unmodified keys,
   * ignored while a text field has focus. That guard is not tidiness here --
   * this screen is almost entirely inputs, and a bracket typed into a memo
   * that navigated away instead would lose everything typed so far.
   *
   * NOT `j`/`k`. Those are the queue's row cursor and mean "move within this
   * list"; reusing them for "leave this entry" would make the same key do two
   * different things one screen apart. Not the arrow keys either: they move
   * the caret inside every field on this page.
   *
   * Deliberately no shortcut for Save or Post. A keystroke that writes to the
   * books, on a screen full of fields, is not something to add without being
   * asked for it.
   */
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'SELECT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }

      if (event.key === '[' && previousId !== undefined) {
        event.preventDefault();
        navigate(`/journals/${previousId}?${queueQuery}`);
      }
      if (event.key === ']' && nextId !== undefined) {
        event.preventDefault();
        navigate(`/journals/${nextId}?${queueQuery}`);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [previousId, nextId, queueQuery, navigate]);

  const posted = entry?.status === 'posted';

  /*
   * §6.9 as revised by migration 0027 — the one state in which this screen is
   * read-only.
   *
   * A posted entry is edited in place until the books are closed through its
   * date. `posted` still decides what the screen OFFERS -- no Post, no rule
   * suggestion, no transfer check -- and `locked` decides whether anything can
   * change at all. A draft is never locked here: 0002's trigger refuses to
   * POST one into a closed period, and the refusal is the server's sentence.
   */
  /*
   * A year-end closing entry (migration 0052) is the other read-only state:
   * it records what a year's balances WERE, is never edited, and is undone by
   * reopening the year in Settings. Folded into `locked` so every control this
   * flag governs -- lines, dates, party, save -- is withheld the same way.
   */
  const closing = entry !== null && typeof entry.closesFiscalYear === 'number';
  /*
   * Someone who may read the books and not edit them (0069) sees the entry
   * as a closed period shows it: every control `locked` governs is withheld,
   * and the notice below says why. The API refuses a change either way.
   */
  const readOnly = !mayEdit;
  /** 0086: a line's account, from the full chart where it is read, or the active one a new entry reads. */
  const accountOf = (accountId: string) =>
    allAccounts.find((candidate) => candidate.id === accountId) ?? accounts.find((candidate) => candidate.id === accountId);
  const holdsStock = (accountId: string) => accountOf(accountId)?.inventoryRole === 'stock';
  /** A stock credit: goods leaving, priced by what they take, never typed (§14.5). */
  const isOutLine = (line: DraftLine) =>
    holdsStock(line.accountId) &&
    line.debit === '' &&
    (line.credit !== '' || lines.some((other) => other.costsKey === line.key || other.fromKey === line.key));
  /** 0088 (§15.3): the stock debit a transfer's goods move into, which names its stock credit. */
  const movedFrom = (line: DraftLine) => (line.fromKey === null ? undefined : lines.find((other) => other.key === line.fromKey));
  const movedInto = (line: DraftLine) => lines.find((other) => other.fromKey === line.key);
  const isExpenseLine = (line: DraftLine) => accountOf(line.accountId)?.type === 'expense';
  /**
   * Round 130's IB-1: only a draft's goods are priced here. A posted entry's
   * goods were priced when it posted and their draws freeze the two lines
   * (0086), so a fresh preview written onto them would make every later save
   * of the entry, a memo or a program, refused for changing drawn goods.
   */
  const pricedHere = (line: DraftLine) => !posted && isOutLine(line);
  /**
   * Round 131's IB-3: a posted line a draw names is frozen by 0086, its
   * account, amounts, goods, fund and project, so it is shown as it is and
   * cannot be removed. Offering the controls would only ever earn a refusal.
   */
  const drawnFixed = (line: DraftLine) => posted && line.drawn;
  /**
   * Round 132's observations, the same kind as IB-3. A posted sale's item line
   * is frozen while its cost entry is live (II-2a): what it sold, and where.
   * And a posted line naming a stock line, its cost or the stock it moved
   * into, cannot be removed alone; its account and program stay editable.
   */
  const saleFixed = (line: DraftLine) => posted && line.itemId !== '' && !holdsStock(line.accountId);
  const goodsFixed = (line: DraftLine) => drawnFixed(line) || saleFixed(line);
  const keepsItsLine = (line: DraftLine) => posted && (line.costsKey !== null || line.fromKey !== null);
  /** A line that names goods: a stock line, or a revenue line that sells an item. */
  const namesGoods = (line: DraftLine) => keepsGoods && (holdsStock(line.accountId) || line.itemId !== '');

  const locked =
    readOnly ||
    closing ||
    (posted && entry !== null && lockedThrough !== null && entry.entryDate <= lockedThrough);

  /** 0086: the business's goods, for the item a line names. */
  useEffect(() => {
    if (!keepsGoods) return;
    let current = true;
    api
      .listItems()
      .then((result) => current && setItems(result.items))
      .catch(() => {
        // The lines still save; the item picker is empty, which the line says.
      });
    return () => {
      current = false;
    };
  }, [keepsGoods]);

  /*
   * 0086 (§14.5): the cost of goods leaving, previewed as they are entered,
   * onto the stock line and its cost line. It is not a promise: a draw made
   * before this entry posts changes it, and posting prices it again from what
   * is there. Keyed on what decides the cost, not on the amounts it writes.
   */
  /** The date goods leave on: the one being typed on a new entry, the entry's own otherwise. */
  const goodsDate = entryDate !== '' ? entryDate : (entry?.entryDate ?? '');
  const previewKey = keepsGoods && goodsDate !== ''
    ? lines
        .filter((line) => !locked && pricedHere(line) && line.itemId !== '' && line.quantity.trim() !== '')
        .map((line) => [line.key, line.itemId, line.quantity.trim(), line.accountId, line.fundId, line.projectId ?? ''].join('|'))
        .concat(goodsDate)
        .join(';')
    : '';
  useEffect(() => {
    if (previewKey === '') return;
    let current = true;
    const wanted = lines.filter((line) => !locked && pricedHere(line) && line.itemId !== '' && line.quantity.trim() !== '');
    for (const line of wanted) {
      api
        .inventoryPreview({
          itemId: line.itemId,
          accountId: line.accountId,
          fundId: line.fundId === '' ? (defaultFund?.id ?? null) : line.fundId,
          projectId: line.projectId === '' ? null : line.projectId,
          quantity: line.quantity.trim(),
          date: goodsDate,
        })
        .then((preview) => {
          if (!current) return;
          const amount = formatAmountInput(preview.totalCents);
          setLines((now) =>
            now.map((candidate) =>
              candidate.key === line.key
                ? { ...candidate, credit: amount, debit: '' }
                : candidate.costsKey === line.key || candidate.fromKey === line.key
                  ? { ...candidate, debit: amount, credit: '' }
                  : candidate,
            ),
          );
          setGoodsErrors((now) => {
            const { [line.key]: _gone, ...rest } = now;
            return rest;
          });
        })
        .catch((caught: unknown) => {
          if (!current) return;
          setGoodsErrors((now) => ({
            ...now,
            [line.key]: caught instanceof ApiError ? caught.message : 'Could not price these goods.',
          }));
        });
    }
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- previewKey carries every input that decides the cost
  }, [previewKey]);


  /*
   * 0087 (issue #65): an invoice or a bill has no cash date. It moves no
   * money; the payment that settles it carries the date the bank moved it,
   * and the cash basis reaches this entry through the match. A receivable or
   * payable line with no bank line beside it is a document, so the field is
   * not offered and the save sends none.
   */
  const roleOf = (accountId: string) =>
    (allAccounts.find((candidate) => candidate.id === accountId) ?? accounts.find((candidate) => candidate.id === accountId))?.accountRole;
  const isDocument =
    lines.some((line) => ['ar_control', 'ap_control'].includes(roleOf(line.accountId) ?? '')) &&
    !lines.some((line) => roleOf(line.accountId) === 'bank');
  /**
   * 0088 (§15.10, round 131): an entry taking goods out of stock moves no money
   * and has no cash date (0086 refuses one where a cost line is); goods received
   * through the bank are a payment and keep theirs, the line 0087 draws.
   */
  // Round 135's NC-1: on an entry that reverses nothing, the database's own condition. A reversal of goods
  // bought through the bank takes them out against its bank line and keeps the bank's date.
  const takesGoodsOut = keepsGoods && (entry?.reversesHeaderId ?? null) === null && lines.some(isOutLine);
  const savedCashDate = entry?.source === 'manual' && (isDocument || takesGoodsOut) ? null : cashDate === '' ? null : cashDate;

  /*
   * An invoice's number can be corrected once it is saved (the operator's
   * request, 2026-09-14). Only on a MANUAL entry -- a bank row's text is the
   * bank's -- and only one that is an invoice: it already carries a number, or
   * it debits the receivables control account.
   */
  const invoiceLike =
    entry !== null &&
    entry.source === 'manual' &&
    // Issue #65: an invoice is one before anything on it says so. A new one
    // is an invoice from the menu's choice, and keeps `kind` in its address
    // once its first save (or a receipt) creates it, until a number or a
    // receivable line says so for good.
    (receivableKind ||
      entry.invoiceReference !== null ||
      lines.some(
        (line) =>
          allAccounts.find((account) => account.id === line.accountId)?.accountRole === 'ar_control' &&
          (parseCents(line.debit) ?? 0) > 0,
      ));

  /**
   * Whether a due date means anything here (migration 0036).
   *
   * BOTH HALVES OF THE BOOKS, which `invoiceLike` alone is not. An invoice
   * DEBITS the receivables control and a bill CREDITS the payables one, and a
   * bill's due date had nowhere to be entered at all -- the Bills screen only
   * displays one. A general journal entry has no counterparty waiting on a
   * date, so the field is not offered there; the New Entry menu asks which kind
   * it is precisely so screens do not have to guess.
   */
  /** 0084 (§B.17.3): an entry with a line on an event-kind account records what the donor received in return. */
  const eventGift = lines.some(
    (line) => allAccounts.find((account) => account.id === line.accountId)?.contributionKind === 'event',
  );
  /** 0084: a receivable on a pledge account is a pledge, and its number is the pledge's. */
  const pledgeLike =
    kind === 'pledge' ||
    lines.some((line) => allAccounts.find((account) => account.id === line.accountId)?.contributionKind === 'pledge');

  const takesTerms =
    invoiceLike ||
    (entry !== null &&
      entry.source === 'manual' &&
      lines.some((line) => {
        const role = allAccounts.find((account) => account.id === line.accountId)?.accountRole;
        return role === 'ap_control' && (parseCents(line.credit) ?? 0) > 0;
      }));

  useEffect(() => {
    if (!usesFunds) return;
    let cancelled = false;
    api
      .listFunds()
      .then((result) => {
        if (!cancelled) setFunds(result.funds);
      })
      // A list that cannot be read leaves each line's fund as stored, and a
      // new line in General; the save says so if a fund is refused.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [usesFunds]);

  useEffect(() => {
    let cancelled = false;
    api
      .listProjects()
      .then((result) => {
        if (!cancelled) setProjects(result.projects);
      })
      // No list means no column: a stored line's project is still sent back.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  /** 0091: the business's phases, for the Phase column. No list means no column; a stored line's phase is still sent back. */
  const [phases, setPhases] = useState<Phase[]>([]);
  useEffect(() => {
    let cancelled = false;
    api
      .listPhases()
      .then((result) => {
        if (!cancelled) setPhases(result.phases);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  /*
   * 0090: the entry's fiscal year's labels, for each line's chips. Read again
   * when the entry's year changes; a save carries a label of another year to
   * its counterpart, so the form keeps what it holds.
   */
  const entryYear = entry?.fiscalYear;
  const [labelYear, setLabelYear] = useState<LabelYear | null>(null);
  useEffect(() => {
    if (entryYear === undefined) return;
    let cancelled = false;
    api
      .listLabels(entryYear)
      .then((result) => {
        if (!cancelled) setLabelYear(result);
      })
      // No list means no picker: a stored line's labels are still sent back.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [entryYear]);

  /*
   * 0081: the column shows for a non-profit, whose every expense line names a
   * program, and for any business that has a project in use.
   */
  const showProjects = nonprofit || projects.some((project) => project.archivedAt === null);
  const defaultProject = projects.find((project) => project.isDefault);
  const projectLabel = projectWord(business);

  /*
   * 0090: the Labels column shows once the entry's year has a label in use, or
   * a line carries one. The first labels are made in Settings; after that a
   * new name typed on a line makes the label in the entry's year.
   */
  const labelInfo = new Map<string, LineLabel & { archived?: boolean }>();
  for (const stored of entry?.lines ?? []) for (const label of stored.labels ?? []) labelInfo.set(label.id, label);
  for (const label of labelYear?.labels ?? []) labelInfo.set(label.id, { ...label, archived: label.archivedAt !== null });
  const mayEditLabels = mayEdit && labelYear !== null && !labelYear.closed;
  /** Stored lines that carried a label: a save sends their labels even when none are left, to clear them. */
  const storedLabelled = new Set((entry?.lines ?? []).filter((stored) => (stored.labels ?? []).length > 0).map((stored) => stored.id));
  const showLabels =
    (labelYear?.labels.some((label) => label.archivedAt === null) ?? false) || lines.some((line) => line.labelIds.length > 0);

  /** A new name typed on a line: the label is made in the entry's year and put on the line. */
  async function addLabel(key: string, name: string) {
    if (labelYear === null) return;
    setSaveError(null);
    try {
      const { label } = await api.createLabel({ fiscalYear: labelYear.fiscalYear, name });
      setLabelYear((current) => (current === null ? current : { ...current, labels: [...current.labels, label] }));
      setLines((current) => current.map((line) => (line.key === key ? { ...line, labelIds: [...line.labelIds, label.id] } : line)));
    } catch (caught) {
      setSaveError(caught instanceof ApiError ? caught.message : 'Could not make the label.');
    }
  }
  /** Whether a line's account may carry a project (§C.2): revenue, expense, or a project-tracking asset. */
  const carriesProject = (accountId: string) => {
    const account = allAccounts.find((candidate) => candidate.id === accountId);
    return account !== undefined && (account.type === 'revenue' || account.type === 'expense' || (account.type === 'asset' && account.tracksProjects === true));
  };
  const isExpense = (accountId: string) => allAccounts.find((candidate) => candidate.id === accountId)?.type === 'expense';
  /** 0091: a phase goes where a project may, and never on a line holding stock. */
  const carriesPhase = (accountId: string) => carriesProject(accountId) && !holdsStock(accountId);
  /** 0091: the column once the business has a phase in use, or a line names one. */
  const showPhases = phases.some((phase) => phase.archivedAt === null) || lines.some((line) => typeof line.phaseId === 'string' && line.phaseId !== '');

  const defaultFund = funds.find((fund) => fund.isDefault);
  const fundCode = (fundId: string) => funds.find((fund) => fund.id === fundId)?.code ?? '';


  const totals = useMemo(() => {
    let debits = 0;
    let credits = 0;
    let invalid = false;
    for (const line of lines) {
      const debit = parseCents(line.debit);
      const credit = parseCents(line.credit);
      if (debit === null || credit === null) invalid = true;
      else {
        debits += debit;
        credits += credit;
      }
    }
    return { debits, credits, difference: debits - credits, invalid };
  }, [lines]);

  const usableLines = lines.filter(
    (line) => line.accountId !== '' && (parseCents(line.debit) || parseCents(line.credit)),
  );
  /** Has the operator entered any amount at all? Separates empty from wrong. */
  const hasAmounts = totals.debits > 0 || totals.credits > 0;

  /*
   * A line carrying an amount but no account is INCOMPLETE, and it used to
   * make this screen lie. `totals` sums every line, while only lines with an
   * account are saved -- so an amount typed against no account balanced the
   * rail, enabled Save and Post, and was then dropped from the request. The
   * server refused the result and the screen had said it was fine.
   *
   * It was reachable before and became easy the moment Fill stopped requiring
   * an account, which is why it is fixed in the same change rather than left
   * as something to notice later.
   */
  const incompleteLines = lines
    .map((line, index) => ({ line, number: index + 1 }))
    .filter(
      ({ line }) =>
        line.accountId === '' && (parseCents(line.debit) || parseCents(line.credit)),
    );

  const balanced =
    !totals.invalid &&
    totals.difference === 0 &&
    totals.debits > 0 &&
    usableLines.length > 0 &&
    incompleteLines.length === 0;

  /**
   * 0079: the funds whose lines do not net to zero. The entry balancing is
   * the rail's; this is the sentence that a save will add lines between the
   * funds, said before it does.
   */
  const fundsOwing = useMemo(() => {
    if (!usesFunds || defaultFund === undefined) return [];
    const net = new Map<string, number>();
    for (const line of usableLines) {
      const fund = line.fundId === '' ? defaultFund.id : line.fundId;
      net.set(fund, (net.get(fund) ?? 0) + (parseCents(line.debit) ?? 0) - (parseCents(line.credit) ?? 0));
    }
    return [...net].filter(([, amount]) => amount !== 0).map(([fund]) => fundCode(fund));
  }, [usesFunds, defaultFund, usableLines, funds]);
  /**
   * Shown only when there is a problem to act on: an unparseable amount, or
   * real amounts that do not balance. A balanced entry says so through the
   * enabled Save and Post button, and an untouched one needs no commentary.
   */
  const showRail =
    totals.invalid || incompleteLines.length > 0 || (hasAmounts && !balanced);

  /**
   * §9.3 — advisory only. The lines on the account this transaction actually
   * arrived on must net to what the bank moved, signed. The rule is a trigger
   * (migration 0014); this only tells the operator before they submit.
   */
  const bankReading = useMemo(() => {
    if (!entry || entry.source === 'manual' || entry.bankAmountCents === null) return null;
    const mapped = bankAccountOf(entry, accounts);
    if (!mapped) return null;
    let net = 0;
    for (const line of lines) {
      if (line.accountId !== mapped.id) continue;
      net += (parseCents(line.debit) ?? 0) - (parseCents(line.credit) ?? 0);
    }
    return { account: mapped, net, target: entry.bankAmountCents };
  }, [entry, accounts, lines]);

  /*
   * §9.3's advisory, as a boolean, because three things now read it: whether
   * the rail appears at all, which status it shows, and whether there is an
   * explanation to offer behind the info button.
   */
  const bankMismatch =
    bankReading !== null && hasAmounts && bankReading.net !== bankReading.target;

  /*
   * The rail appears for anything the operator can act on -- INCLUDING a bank
   * mismatch on an entry whose debits and credits already agree.
   *
   * That case used to hide the rail and rely on a separate paragraph below it.
   * Now the paragraph is behind an info button ON the rail, so leaving the
   * old condition alone would have made the warning vanish for a balanced
   * entry the server is about to refuse -- the one shape where the screen
   * looks finished and is not.
   */
  const railVisible = showRail || bankMismatch;

  const party = parties.find((candidate) => candidate.id === partyId);
  const partyName = party?.name ?? null;

  /**
   * §7 — the party's role decides which accounts are listed first.
   *
   * Grouping only. A customer's revenue accounts come under a Revenue heading
   * at the top; a vendor's, charity's or employee's under Expense. Nothing is
   * promoted out of its group and nothing is preselected — see the Combobox
   * header for why that distinction is load-bearing.
   *
   * Charity and employee get no finer ordering than vendor, and that is
   * honest rather than lazy: `account_role` is reserved for §6.2's cash-basis
   * conversion, so there is no way to identify contribution or payroll
   * accounts specifically without inventing new metadata. The better answer is
   * history — the accounts this party has been coded to before — which needs
   * posted entries to exist and is the on-ramp to §9.7 rather than a rival
   * taxonomy.
   */
  const accountGroupOrder = useMemo(() => {
    if (!party) return [];
    if (party.isCustomer) return ['Revenue', 'Asset'];
    if (party.isVendor || party.isCharity || party.isEmployee) return ['Expense', 'Asset'];
    return [];
  }, [party]);

  /**
   * §7 — the roles the chosen party holds, or null when it holds none.
   *
   * Null rather than the string "no role set", because the two render
   * differently: roles get badge chrome, the absence of roles gets plain muted
   * text. A badge is a container for a value and putting one round "no role
   * set" asserts a value where there is not one.
   */
  const partyRoles = useMemo(() => (party ? roleLabels(party) : null), [party]);

  const accountOptions: ComboboxOption[] = useMemo(
    () =>
      accounts.map((account) => ({
        id: account.id,
        label: `${account.code} ${account.name}`,
        // Capitalised for display; the API returns lowercase types.
        group: account.type.charAt(0).toUpperCase() + account.type.slice(1),
      })),
    [accounts],
  );

  /**
   * Inline creation, because coding a bank backlog means meeting most
   * parties for the first time mid-entry.
   *
   * A FORM, not a silent create. The first version posted immediately with a
   * role inferred from whether a revenue account happened to be coded — which,
   * on an entry with no lines yet, silently made every new party a vendor. The
   * comment claimed the role was "pre-checked on a form the operator is looking
   * at"; no form existed, so it was not a default anybody could see, it was the
   * system classifying on their behalf. §6.5 is explicit that the operator
   * classifies and the system does not infer, and with no party-management
   * screen yet a wrong role could not be corrected anywhere.
   *
   * The coding still supplies the DEFAULTS — that part was worth keeping — but
   * they are checkboxes on screen, and unchecking them is one click.
   */
  function beginCreateParty(name: string) {
    const usesRevenue = lines.some((line) => {
      const account = accounts.find((candidate) => candidate.id === line.accountId);
      return account?.type === 'revenue';
    });
    setDraftParty({
      name,
      isCustomer: usesRevenue,
      isVendor: !usesRevenue,
      isEmployee: false,
      isCharity: false,
    });
  }

  /*
   * §6.10 — creating an account mid-coding.
   *
   * Same shape as the party path above and for the same reason: you meet an
   * account you have not got while looking at the transaction that needs it,
   * and the alternative is abandoning a half-coded entry to go to another
   * screen.
   *
   * NO DEFAULTS INFERRED FROM THE CODING, which is where this differs from
   * beginCreateParty. A party's role can be guessed from whether the entry
   * touches revenue and corrected in one click on the parties screen. An
   * account's type and normal balance cannot be corrected at all once it
   * carries posted entries -- the server refuses both fields on update,
   * because changing them reverses the sign of history. A wrong guess here is
   * permanent, so the form asks.
   */
  async function commitCreateAccount(body: CreateAccountBody) {
    if (!draftAccount) return;
    setBusy(true);
    setSaveError(null);
    try {
      const { account } = await api.createAccount(body);
      setAccounts((current) => [...current, account]);
      update(draftAccount.lineKey, { accountId: account.id });
      setNotice(`Added ${account.code} ${account.name}`);
      setDraftAccount(null);
    } catch (caught) {
      setSaveError(
        caught instanceof ApiError ? caught.message : 'Could not add that account.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function commitCreateParty() {
    if (!draftParty) return;
    const name = draftParty.name.trim();
    if (name === '') return;
    setBusy(true);
    try {
      const created = await api.createParty({ ...draftParty, name });
      setParties((current) =>
        [...current, created.party].sort((a, b) => a.name.localeCompare(b.name)),
      );
      setPartyId(created.party.id);
      setDraftParty(null);
      setNotice(`Added ${created.party.name}`);
    } catch (caught) {
      setSaveError(caught instanceof ApiError ? caught.message : 'Could not add that party.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * §7 and §17 — which entry a dropped receipt belongs to.
   *
   * The upload itself lives in `ReceiptDropzone`, shared with the Entries
   * list. This is the half only the editor can answer, because attaching to an
   * entry that does not exist yet CREATES it.
   *
   * A receipt is stored against a header id and a new entry has none, so
   * something has to give. Saving first is the honest resolution: dropping a
   * file is a deliberate act, not a stray click, so a row created by it is a
   * row the operator meant to create. That is the same test the rest of this
   * screen uses -- nothing is written until you do something that means it.
   *
   * The alternative, holding the bytes in memory until the first save, buys a
   * marginally tidier failure case and costs a second upload path that only
   * ever runs once per entry.
   *
   * Null when the create was refused. The create has already said why.
   */
  async function resolveReceiptTarget(): Promise<string | null> {
    setSaveError(null);
    if (!isNew) return id!;
    const created = await createFromForm();
    return created?.id ?? null;
  }

  /**
   * A header id that is not this route's is one `resolveReceiptTarget` just
   * created, and the route has to follow it before the load can refetch an
   * attachment list that includes the new receipt.
   */
  function receiptAttached(attachments: Attachment[], headerId: string) {
    // Called once for a whole drop, so a new entry navigates once, after every
    // file is recorded -- the load then lists all of them.
    const said = `Attached ${describeAttached(attachments)}`;
    if (headerId !== id) {
      pendingNotice.current = said;
      /*
       * Issue #65: the save that made this draft keeps only lines with an
       * account and an amount, so a receivable line still waiting for its
       * amount would vanish under the operator's hands. The form's lines go
       * with it instead, and the load puts them back on the draft, whose lines
       * the next save replaces wholesale.
       */
      carriedLines.current = lines;
      navigate(`/journals/${headerId}${kindSuffix}`, { replace: true });
      return;
    }
    setReceipts((current) => [...current, ...attachments]);
    setNotice(said);
  }

  /*
   * Clicking a receipt OPENS it, in the same overlay Triage uses.
   *
   * It used to fetch a signed URL and `window.open` it, which the browser
   * turns into a save -- so the editor downloaded a file while the queue two
   * clicks away showed the same receipt in a frame. Two screens, one gesture,
   * two different outcomes. The overlay carries its own Download button, so
   * the save is still one click and is now the one the operator asked for.
   *
   * No URL is fetched here any more: the overlay issues its own `inline`
   * presign, and a signed URL is a 300-second capability not worth spending
   * twice.
   */
  const [viewing, setViewing] = useState<Attachment | null>(null);

  /*
   * §9.4 — REORDERING THE LINES.
   *
   * The order is not cosmetic and it is not derived: `journal_details` carries
   * `line_number`, and `saveDraft` renumbers by array index, so the order on
   * this screen is the order in the books, in the Excel export and in every
   * report that reads a line. That is why this is a real edit and not a view
   * preference -- and why nothing extra has to be sent for it to persist.
   *
   * WHY A DRAG HANDLE AND NOT THE WHOLE ROW
   *
   * The row is full of controls. A row that is itself draggable swallows the
   * text selection inside its own amount inputs -- the operator drags to
   * select "12.50" and moves the line instead. The handle is the one part of
   * the row that does nothing else.
   *
   * KEYBOARD IS NOT OPTIONAL HERE, and it is not automatic: HTML5 drag and
   * drop has no keyboard equivalent at all, so a drag-only implementation
   * would put an editing action behind a pointer. Alt with an arrow key moves
   * the focused line, and focus follows it, which is also the fastest way to
   * do it for anyone who does not want to drag.
   */
  const [dragging, setDragging] = useState<number | null>(null);

  function moveLine(from: number, to: number) {
    if (from === to) return;
    setLines((current) => {
      if (to < 0 || to >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved!);
      return next;
    });
  }

  async function removeReceipt(attachment: Attachment) {
    try {
      await api.deleteAttachment(attachment.id);
      setReceipts((current) => current.filter((candidate) => candidate.id !== attachment.id));
      setNotice(`Removed ${attachment.originalFilename}`);
    } catch (caught) {
      setSaveError(caught instanceof ApiError ? caught.message : 'Could not remove that receipt.');
    }
  }

  /*
   * §7 — when is an alias worth offering?
   *
   * Only when the operator has CHOSEN a party (so `partyMatch` is null, and
   * this is their assertion rather than ours), the entry carries a
   * counterparty, and that counterparty is not already this party's name. The
   * last condition is what stops it offering an alias that would be refused as
   * redundant the moment it was sent.
   */
  const chosenParty = parties.find((candidate) => candidate.id === partyId);
  const aliasWorthOffering =
    !locked &&
    partyMatch === null &&
    chosenParty !== undefined &&
    entry?.counterparty != null &&
    entry.counterparty.trim() !== '' &&
    entry.counterparty.trim().toLowerCase() !== chosenParty.name.trim().toLowerCase()
      ? entry.counterparty.trim()
      : null;

  useEffect(() => {
    setAliasOffer(aliasWorthOffering);
  }, [aliasWorthOffering]);

  async function saveAlias() {
    if (aliasOffer === null || partyId === '') return;
    try {
      const { alias } = await api.addPartyAlias(partyId, aliasOffer);
      setSavedAlias({ id: alias.id, alias: alias.alias });
      /*
       * TWO FACTS, AND THE TOAST ONLY CARRIES ONE OF THEM.
       *
       * `savedAlias` is what Undo acts on, and it lives exactly as long as the
       * toast: the toast dismisses itself on a clock and clears it. `aliasDone`
       * is the mapping existing, which does not expire. Setting only the first
       * made the offer come back the moment the toast faded, and the second
       * click hit the server's duplicate check -- so the operator was told the
       * alias was already there by the very screen that had just offered it.
       * Undo clears both, which is what puts the offer back legitimately.
       */
      setAliasDone(true);
    } catch (caught) {
      /*
       * ALREADY THERE IS NOT A FAILURE, and treating it as one is what put a
       * red line under an offer that stayed on screen inviting the same click
       * again. The server refuses a duplicate alias and a name that is already
       * a party's own; in both cases the mapping the operator wanted EXISTS.
       * So the offer is withdrawn and the sentence is a neutral toast, not an
       * error under a button that cannot work.
       */
      if (
        caught instanceof ApiError &&
        (caught.code === 'duplicate_alias' || caught.code === 'is_party_name')
      ) {
        setAliasDone(true);
        setNotice(caught.message);
        return;
      }
      setSaveError(
        caught instanceof ApiError ? caught.message : 'Could not remember that alias.',
      );
    }
  }

  async function undoAlias() {
    if (!savedAlias) return;
    try {
      await api.removePartyAlias(savedAlias.id);
      setSavedAlias(null);
      setAliasDone(false);
      setNotice(`Removed the alias “${savedAlias.alias}”`);
    } catch (caught) {
      setSaveError(
        caught instanceof ApiError ? caught.message : 'Could not remove that alias.',
      );
    }
  }

  /*
   * §9.7 — applying a suggestion, and why it is a button.
   *
   * IT NOW APPLIES ITSELF ON OPEN, and that is a change of policy made
   * deliberately on 2026-09-12 at the operator's instruction.
   *
   * §9.7 step 4a says not to pre-fill, and its actual argument is about SPLIT
   * RATIOS: the engine cannot tell a stable proportion from an accidental one,
   * so a tip that is always a percentage and a warehouse run split between
   * supplies and meals look identical to it. That reasoning is correct and it
   * does not reach a two-line bank entry, where nothing is being inferred --
   * the account comes from a rule the operator wrote, and the amount is the
   * one the bank stated.
   *
   * The precedent is already here: the party fills itself from an alias on
   * load, with a line saying which alias did it. This is the same shape.
   *
   * WHAT IS STILL TRUE. The provenance line stays, naming the pattern that
   * matched, and a fill is reversible where a post is not. The risk that
   * matters is a wrong ACCOUNT reaching a tax return, and that is the field
   * the rule already suggested before this change -- filling it in earlier
   * does not widen the exposure.
   *
   * It lands on the first line with NO account, and never over one the
   * operator already chose.
   */
  const lineAwaitingAccount = posted
    ? undefined
    : lines.find((line) => line.accountId === '');

  function applyRule() {
    if (!ruleSuggestion || !lineAwaitingAccount) return;

    /*
     * The account AND the amount.
     *
     * The amount needs no configuration on the rule, because for a two-line
     * bank entry there is exactly one amount it could be: the one the bank
     * reported. Migration 0014 refuses to post an entry whose lines on its own
     * mapped account do not net to it, so any other figure would be rejected.
     *
     * `fillFor` already computes the side and the figure for this line -- the
     * same arithmetic the Fill button offers -- so this is that button pressed
     * one step earlier rather than a second opinion about the number.
     */
    const amount = fillFor(lineAwaitingAccount);
    update(lineAwaitingAccount.key, {
      accountId: ruleSuggestion.accountId,
      ...(amount !== null && {
        [amount.side]: formatAmountInput(amount.cents),
      }),
      // 0081 (§C.6): the rule's program on the line it categorizes.
      ...(ruleSuggestion.projectId != null && { projectId: ruleSuggestion.projectId }),
      // 0091: and its phase.
      ...(ruleSuggestion.phaseId != null && { phaseId: ruleSuggestion.phaseId }),
    });
    // ...and its fund on every line still in the default, so the entry stays in one fund.
    const ruleFund = ruleSuggestion.fundId;
    if (ruleFund != null) {
      setLines((current) => current.map((candidate) => (candidate.fundId === '' ? { ...candidate, fundId: ruleFund } : candidate)));
    }
  }


  /*
   * §9.7 — when is a RULE worth offering?
   *
   * THE CAPTURE POINT IS THE DESIGN, and it is the lesson the party aliases
   * taught in this same file: a settings screen nobody visits collects
   * nothing, and the operator goes on coding by hand forever. A rule is
   * offered where the mapping is actually being made -- the operator has just
   * coded an entry to an account, and nothing yet codes that counterparty.
   *
   * Offered only when:
   *   - no rule matched already, so this is not a re-offer of what fired;
   *   - the entry carries a counterparty to match on;
   *   - exactly ONE non-bank account has been coded, so "this counterparty
   *     means that account" is a claim the entry actually supports. A
   *     four-line split does not imply a one-line rule, and §16.2 defers
   *     multi-line rules deliberately.
   */
  /*
   * THE ENTRY'S OWN BANK ACCOUNT IS NOT AN ANSWER TO "WHAT WAS THIS FOR".
   *
   * It is on EVERY Mercury entry by construction -- migration 0014 refuses to
   * post one without it -- so it carries no information about the transaction,
   * and a rule pointing at it would suggest the bank account as the coding for
   * every future entry from that counterparty. Exactly backwards.
   *
   * This line is why the offer was appearing on untouched entries. The
   * condition asked for one coded account and the comment above said "non-bank",
   * but nothing enforced it: before the bank line was derived, the only account
   * on a fresh entry WAS the operator's categorisation, so the two read the
   * same. The derivation made them different and the offer started firing on
   * load, proposing "always code A21 USA, Inc. to 1000 Bank — Operating".
   *
   * Excluded by identity, not by `account_role`. A credit-card payment is
   * 1000 to 2100 and both carry the bank role; excluding the role would kill a
   * legitimate rule for the card account. Only the account THIS entry's money
   * moved on is uninformative.
   */
  const codedAccountIds = [
    ...new Set(
      lines
        .map((line) => line.accountId)
        .filter((accountId) => accountId !== '' && accountId !== bankReading?.account.id),
    ),
  ];
  const ruleWorthOffering =
    !posted &&
    ruleSuggestion === null &&
    savedRule === null &&
    !ruleDone &&
    entry?.counterparty != null &&
    entry.counterparty.trim() !== '' &&
    codedAccountIds.length === 1
      ? { counterparty: entry.counterparty.trim(), accountId: codedAccountIds[0]! }
      : null;

  /*
   * §9.7 — apply the rule as soon as it arrives, on an UNCODED entry.
   *
   * `codedAccountIds` and not `lines`, and that distinction is the whole
   * guard. A mapped Mercury entry opens with its BANK line already filled,
   * derived from the account the money moved on -- so "does any line have an
   * account" is true before the operator has touched anything, and the first
   * version of this effect blocked itself on every bank entry.
   *
   * `codedAccountIds` excludes the entry's own bank account for exactly this
   * reason: it answers "what has the operator chosen", which is the question.
   *
   * A ref rather than state, so clearing the line by hand does not put it
   * straight back.
   *
   * THAT GUARD CATCHES NOTHING TODAY, and removing it fails no test. Blank
   * lines are never saved -- `usableLines` drops them -- so a loaded entry
   * either has an account on every line, in which case `lineAwaitingAccount`
   * is already undefined and `applyRule` no-ops, or it is uncoded. It is kept
   * for the same reason the cash-basis service keeps its redundant
   * `cash_date IS NOT NULL`: it states the rule where the rule applies, and it
   * is the thing that would still hold if blank lines ever became storable.
   * Recorded as unrun rather than left looking load-bearing.
   */
  const ruleApplied = useRef<string | null>(null);
  useEffect(() => {
    if (posted || isNew || !ruleSuggestion) return;
    if (ruleApplied.current === ruleSuggestion.ruleId) return;
    if (codedAccountIds.length > 0) return;
    ruleApplied.current = ruleSuggestion.ruleId;
    applyRule();
    // `codedAccountIds` is deliberately absent: this runs when a suggestion
    // arrives, not on every keystroke in a line.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleSuggestion, posted, isNew]);

  async function saveRule() {
    if (!ruleWorthOffering) return;
    try {
      const { rule } = await api.createCodingRule({
        matchType: 'contains',
        pattern: ruleWorthOffering.counterparty,
        accountId: ruleWorthOffering.accountId,
      });
      setSavedRule({ id: rule.id, pattern: rule.pattern });
      // Same two facts as the alias above, and the same bug: the toast is the
      // lifetime of Undo, not the lifetime of the rule.
      setRuleDone(true);
    } catch (caught) {
      // Same reading as the alias: a pattern that already codes to something
      // is a mapping that exists, not a failure of this click.
      if (caught instanceof ApiError && caught.code === 'duplicate_pattern') {
        setRuleDone(true);
        setNotice(caught.message);
        return;
      }
      setSaveError(
        caught instanceof ApiError ? caught.message : 'Could not remember that rule.',
      );
    }
  }

  /**
   * §9.1 — exclude this header and record what accounts for the movement.
   *
   * The operator's confirmation of a suggestion, never automatic. The server
   * re-derives the pairing, so a stale id on this screen is refused rather
   * than written.
   */
  async function excludeAsTransfer() {
    if (!counterpart) return;
    try {
      await api.recordCounterpart(id, counterpart.headerId);
      // Straight to the entry that DOES record the movement. Staying here
      // would leave the operator looking at a header they just excluded.
      navigate(`/journals/${counterpart.headerId}${queueQuery ? `?${queueQuery}` : ''}`);
    } catch (caught) {
      setSaveError(
        caught instanceof ApiError ? caught.message : 'Could not exclude that entry.',
      );
    }
  }

  async function undoRule() {
    if (!savedRule) return;
    try {
      await api.deleteCodingRule(savedRule.id);
      setSavedRule(null);
      setRuleDone(false);
      setNotice(`Deleted the rule for “${savedRule.pattern}”`);
    } catch (caught) {
      setSaveError(
        caught instanceof ApiError ? caught.message : 'Could not delete that rule.',
      );
    }
  }

  /**
   * §9.4 — the fill affordance.
   *
   * Most entries here are two lines: one bank line and one contra line for the
   * same amount, so the second amount is arithmetic, not a decision. Typing it
   * again is the most repeated action on this screen.
   *
   * Offered as a BUTTON rather than filled in silently. FINDINGS.md's warning
   * is about suggestions -- a value that is right most of the time trains the
   * eye to skip the field -- and a difference is not a guess, so the warning is
   * weaker here. But a number that appears on its own can still hide a coding
   * error: put 189.04 against the wrong account and a silent fill makes the
   * entry balance, which reads as confirmation. A click means the operator
   * chose it.
   *
   * The side is determined, not chosen: if debits lead, the fill is a credit.
   */
  function fillFor(line: DraftLine): { side: 'debit' | 'credit'; cents: number } | null {
    if (locked) return null;
    /*
     * The account is NOT required. Offering an amount for a line that cannot
     * be posted yet breaks the usual rule -- but two-line entries are the
     * overwhelming majority here, and in one the second amount is known the
     * instant the first is typed. Making the operator choose an account before
     * the app will do arithmetic it has already done is friction for a case
     * that is rare.
     *
     * Safe because the entry still cannot post: an amount without an account
     * now blocks `balanced` outright and the rail names the line.
     */
    if (parseCents(line.debit) || parseCents(line.credit)) return null;
    if (totals.invalid) return null;

    /*
     * Nothing entered anywhere yet, but the bank already told us the amount.
     *
     * Without this, Fill needed some OTHER line to carry a value first, so on
     * a fresh two-line draft it appeared nowhere and the operator typed the
     * first figure by hand -- copying a number printed at the top of the same
     * screen. Two-line entries are the overwhelming majority here and the
     * total is known before the coding is.
     *
     * The side is still determined rather than chosen. `target` is the signed
     * amount the BANK account must net (§9.3): negative when money left. So
     * the bank's own line takes the side that reaches the target, and every
     * other line takes the opposite. A line with no account yet is treated as
     * "not the bank" -- that is the categorisation line, which is the one the
     * operator fills in first.
     */
    if (totals.difference === 0) {
      if (hasAmounts || !bankReading || bankReading.target === 0) return null;
      const isBankLine = line.accountId === bankReading.account.id;
      const negative = bankReading.target < 0;
      return {
        side: isBankLine === negative ? 'credit' : 'debit',
        cents: Math.abs(bankReading.target),
      };
    }

    return {
      side: totals.difference > 0 ? 'credit' : 'debit',
      cents: Math.abs(totals.difference),
    };
  }

  const update = useCallback((key: string, patch: Partial<DraftLine>) => {
    setLines((current) =>
      current.map((line) =>
        line.key === key
          ? { ...line, ...patch }
          : // 0088 (§15.3): a transfer moves the same goods it takes out.
            line.fromKey === key && (patch.itemId !== undefined || patch.quantity !== undefined)
            ? {
                ...line,
                ...(patch.itemId !== undefined && { itemId: patch.itemId }),
                ...(patch.quantity !== undefined && { quantity: patch.quantity }),
              }
            : line,
      ),
    );
  }, []);

  /**
   * §17 — create the header and its lines from what is on the form.
   *
   * Two calls rather than one, because `POST /journals` creates a header and
   * `PUT /journals/:id/lines` replaces its lines, and neither was built to do
   * the other's job. They are not atomic: a header can exist with no lines if
   * the second fails. That is survivable in a way the reverse would not be --
   * an empty draft is visible in the queue and deletable, where orphaned lines
   * would be neither -- and migration 0009 refuses to post a header with none.
   *
   * IT DOES NOT NAVIGATE. Both callers do, afterwards, because attaching a
   * receipt has to finish its upload against the new id before the route
   * changes and the load effect refetches the attachment list.
   */
  async function createFromForm(): Promise<{ id: string; version: number } | null> {
    setBusy(true);
    setSaveError(null);
    try {
      const { entry: created } = await api.createJournal({
        entryDate,
        partyId: partyId === '' ? null : partyId,
        memo: memo.trim() === '' ? null : memo.trim(),
        invoiceReference: invoiceReference.trim() === '' ? null : invoiceReference.trim(),
      });
      const result = await api.saveJournalLines(created.id, {
        expectedVersion: created.version,
        header: {
          cashDate: savedCashDate,
          // Issue #65: a new invoice offers its due date, so its first save keeps it.
          ...(takesTerms && { dueDate: dueDate === '' ? null : dueDate }),
          ...(eventGift && { goodsServicesCents: goodsServices.trim() === '' ? null : (parseCents(goodsServices) ?? null) }),
        },
        lines: usableLines.map((line) => ({
          accountId: line.accountId,
          debitCents: parseCents(line.debit) ?? 0,
          creditCents: parseCents(line.credit) ?? 0,
          ...goodsFields(line, usableLines),
        })),
      });
      return { id: created.id, version: result.entry.version };
    } catch (caught) {
      setSaveError(
        caught instanceof ApiError ? caught.message : 'Could not create that entry.',
      );
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function save(): Promise<number | null> {
    /*
     * §17 — a new entry is CREATED by its first save, header and lines
     * together, and the screen then becomes an ordinary editor on the row it
     * just made.
     *
     * Two calls rather than one, because `POST /journals` creates a header and
     * `PUT /journals/:id/lines` replaces its lines, and neither was built to
     * do the other's job. They are not atomic: a header can exist with no
     * lines if the second call fails. That is survivable in a way the reverse
     * would not be -- an empty draft is visible in the queue and deletable,
     * where orphaned lines would be neither -- and a header with no lines
     * cannot be posted, because migration 0009 refuses it.
     */
    if (isNew) {
      const created = await createFromForm();
      if (created === null) return null;
      pendingNotice.current = 'Saved';
      // Replace rather than push: Back should return to where the entry was
      // started, not to a blank form that no longer means anything. An
      // invoice stays one (issue #65).
      navigate(`/journals/${created.id}${kindSuffix}`, { replace: true });
      return created.version;
    }

    if (version === null) return null;
    setBusy(true);
    setSaveError(null);
    try {
      const body: SaveLinesBody = {
        expectedVersion: version,
        header: {
          memo: memo.trim() === '' ? null : memo.trim(),
          cashDate: savedCashDate,
          // counterparty is deliberately NOT sent. It holds what the bank
          // reported; the party is the operator's classification of it.
          partyId: partyId === '' ? null : partyId,
          // Only on an invoice, which is the only place the field is offered.
          ...(invoiceLike && {
            invoiceReference: invoiceReference.trim() === '' ? null : invoiceReference.trim(),
          }),
          // Sent only where the field is offered, for the same reason: the key
          // being ABSENT leaves the stored date alone, while sending null
          // clears it. A general journal entry must not silently erase terms.
          ...(takesTerms && { dueDate: dueDate === '' ? null : dueDate }),
          ...(eventGift && { goodsServicesCents: goodsServices.trim() === '' ? null : (parseCents(goodsServices) ?? null) }),
        },
        lines: usableLines.map((line) => ({
          // See DraftLine.id: on a posted entry this is update-in-place.
          ...(line.id !== undefined && { id: line.id }),
          accountId: line.accountId,
          debitCents: parseCents(line.debit) ?? 0,
          creditCents: parseCents(line.credit) ?? 0,
          // Empty is General: the server puts a new line there, and a stored
          // line always carries its own fund here (fromStored).
          ...(usesFunds && line.fundId !== '' && { fundId: line.fundId }),
          // 0081: empty is not chosen (the server's default or the line's own);
          // null is none, chosen; an id is that project.
          ...(line.projectId !== '' && { projectId: line.projectId }),
          // 0091: the same three states for the phase.
          ...(line.phaseId !== '' && { phaseId: line.phaseId }),
          // 0086: the line's goods, and which stock line a cost line is the cost of.
          ...goodsFields(line, usableLines),
          // 0090: sent whenever the line carries a label or its stored copy did,
          // so a label taken off is cleared; otherwise left out, which keeps a
          // stored line's labels (none) and gives a new line none.
          ...((line.labelIds.length > 0 || storedLabelled.has(line.id ?? '')) && { labelIds: line.labelIds }),
        })),
      };
      const result = await api.saveJournalLines(id, body);
      // The server's version, not version + 1.
      setVersion(result.entry.version);
      setNotice('Saved');
      /*
       * A posted entry is read back, because a line added in this save has no
       * id on the form yet. Without the read, the NEXT save would send that
       * line as new again and remove the stored copy -- a history of
       * removals for an edit that changed nothing, and a refusal the moment
       * that line is matched. A draft needs no read: its lines are replaced
       * wholesale whatever the form sends.
       */
      // In a business that keeps funds a draft is read back too: the save may
      // have written lines between funds, which only the read shows.
      if (posted || usesFunds) await reloadEntry();
      return result.entry.version;
    } catch (caught) {
      // The form is deliberately left untouched. See the header: replacing
      // what the operator typed to resolve a conflict they have not seen is
      // worse than making them decide.
      showRefusal(caught, 'Could not save.');
      return null;
    } finally {
      setBusy(false);
    }
  }

  /**
   * The server's sentence, and the matches in the way when that is the reason.
   *
   * `matched_line` carries the LIVE matches in `detail.matches`, and they
   * replace the list on screen so each can be undone where the operator is.
   * `matched_line_history` is different in kind: the match is already undone
   * and its record still holds the line, so the line can be changed but not
   * removed. There is nothing to undo, the server's sentence says exactly
   * that, and no list is offered.
   */
  function showRefusal(caught: unknown, fallback: string) {
    setSaveError(caught instanceof ApiError ? caught.message : fallback);
    if (caught instanceof ApiError && caught.code === 'matched_line') {
      const listed = (caught.detail as { matches?: unknown } | undefined)?.matches;
      if (Array.isArray(listed)) setMatches(listed as EntryMatch[]);
    }
  }

  /**
   * Read the entry back after a write that changed its lines' ids or status.
   *
   * Replaces the lines, so it is called only after a SUCCESSFUL save or post,
   * when the form and the server already agree. A refused write never reaches
   * this -- see the header on not discarding the operator's work.
   */
  async function reloadEntry() {
    try {
      const { entry: fresh } = await api.getJournal(id);
      setEntry(fresh);
      setVersion(fresh.version);
      if (fresh.lines.length > 0) setLines(storedLines(editable(fresh.lines)));
      setInterfundLines(fresh.lines.filter((line) => line.interfund === true));
      setHistory(null);
      setHistoryError(null);
    } catch {
      setSaveError(
        'Saved, but the entry could not be read back. Reload the page before changing it again.',
      );
      return;
    }
    api
      .getJournalMatches(id)
      .then((result) => setMatches(result.matches))
      .catch(() => {});
  }

  /*
   * §9.4 — save, then post, on one click.
   *
   * THERE IS NO CONFIRMATION ANY MORE, and that follows from migration 0027
   * rather than from impatience. The dialog existed because posting was
   * permanent: the only correction was a reversing entry that was permanent
   * too. The operator decided on 2026-09-13 that a posted entry is edited in
   * place until the books close, so a mistake here is fixed by opening the
   * entry and changing it. A dialog in front of a reversible act is the
   * accept-by-reflex problem ConfirmPost's own header warns about.
   *
   * Bulk posting on Entries keeps its confirmation: forty entries at once is a
   * different act, and the dialog there shows every line.
   */
  async function saveAndPost() {
    /*
     * A NEW entry has no id in this route until its first save creates one,
     * and `save` hands back only a version before navigating away. Posting
     * with the route's `id` would post to "new". So the create is done here,
     * the post goes to the id it returned, and the route follows afterwards,
     * carrying the outcome across the reset the navigation causes.
     */
    if (isNew) {
      const created = await createFromForm();
      if (created === null) return;
      setBusy(true);
      try {
        await api.postJournal(created.id, created.version);
        pendingNotice.current = 'Posted';
      } catch (caught) {
        // The draft exists; the refusal is shown on it rather than lost.
        pendingError.current =
          caught instanceof ApiError ? caught.message : 'Saved, but could not post.';
      } finally {
        setBusy(false);
      }
      navigate(`/journals/${created.id}`, { replace: true });
      return;
    }

    const current = await save();
    if (current === null) return;
    setBusy(true);
    try {
      const result = await api.postJournal(id, current);
      setVersion(result.entry.version);
      setEntry((previous) => (previous ? { ...previous, status: 'posted' } : previous));
      setNotice('Posted');
      // The lines came from the form, which holds no ids for lines this save
      // created. Editing the entry next needs them.
      await reloadEntry();
    } catch (caught) {
      setSaveError(caught instanceof ApiError ? caught.message : 'Could not post.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * §5.5 as revised by 0027 — delete an entry entered by hand, then return to
   * Entries.
   *
   * Offered only where the server would accept it (see `deletable`), so a
   * refusal here is a state the screen could not see: a match, or a change in
   * another tab. The version is the one this screen holds, never computed.
   */
  async function deleteEntry() {
    if (version === null) return;
    setBusy(true);
    setSaveError(null);
    try {
      await api.deleteJournal(id, version);
      navigate(`/triage${queueQuery ? `?${queueQuery}` : ''}`);
    } catch (caught) {
      setConfirmingDelete(false);
      showRefusal(caught, 'Could not delete this entry.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * §9.6 — reverse a posted entry, then open the reversal it made.
   *
   * The reversal is a NEW posted entry dated the tenant's today with every debit
   * and credit swapped, so the two cancel and both stay in the books. Dated
   * today so a correction never lands in a closed period -- which is why this is
   * offered when the books are closed through the entry, when nothing else is.
   * The notice names the date the correction took, as §9.6 asks.
   */
  async function reverseEntry() {
    if (reversing === null || reversing.trim() === '') return;
    setBusy(true);
    setSaveError(null);
    try {
      const { reversal } = await api.reverseJournal(id, reversing.trim());
      setReversing(null);
      // 0090: a label with no counterpart in the reversal's year is left off, and said so.
      const leftOff = reversal.labelsLeftOff ?? [];
      pendingNotice.current =
        `Reversed. The reversal is dated ${reversal.entryDate}.` +
        (leftOff.length === 0
          ? ''
          : ` Its lines do not carry ${[...new Set(leftOff.map((row) => row.label))].join(', ')}, which ${leftOff.length === 1 ? 'has' : 'have'} no label in the reversal's fiscal year.`);
      navigate(`/journals/${reversal.reversalHeaderId}`);
    } catch (caught) {
      showRefusal(caught, 'Could not reverse this entry.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * §6.3 — undo one payment match from the entry, with the reason the server
   * requires.
   *
   * The same call and the same reason prompt as the Matching screen. The form
   * is left exactly as it was, so the save that was refused can simply be
   * pressed again: undoing the match is the operator's answer to the refusal,
   * not a reason to lose the edit that caused it.
   */
  async function undoMatch() {
    if (undoing === null || undoing.reason.trim() === '') return;
    const { clearingId, reason } = undoing;
    setBusy(true);
    setSaveError(null);
    try {
      await api.voidClearing(clearingId, reason.trim());
      setMatches((current) => current.filter((match) => match.clearingId !== clearingId));
      setUndoing(null);
      setNotice('Match undone');
    } catch (caught) {
      setSaveError(caught instanceof ApiError ? caught.message : 'Could not undo that match.');
    } finally {
      setBusy(false);
    }
  }

  /*
   * §5.7 — the History panel reads on first open, and again after a write.
   *
   * Collapsed by default and fetched lazily: an entry's history is looked at
   * rarely, and a request on every open of every entry in a coding session
   * buys nothing. A write clears `history`, so an open panel refetches.
   */
  useEffect(() => {
    if (isNew || !historyOpen || history !== null || historyError !== null) return;
    let cancelled = false;
    api
      .getJournalHistory(id)
      .then((result) => {
        if (!cancelled) setHistory(result.changes);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setHistoryError(
            caught instanceof ApiError ? caught.message : 'Could not read the history.',
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew, historyOpen, history, historyError]);

  /*
   * Delete is offered where the server accepts it: an entry entered by hand,
   * not in a closed period, not either side of a reversal. A bank transaction
   * is never deletable -- the next sync would bring it back -- so it gets no
   * control at all rather than one that is refused.
   */
  const deletable =
    !isNew &&
    entry !== null &&
    entry.source === 'manual' &&
    !locked &&
    entry.reversesHeaderId === null &&
    entry.reversedByHeaderId === null;

  /*
   * Reverse is offered on a posted entry that is not already either side of a
   * reversal -- the database allows one reversal per entry, and reversing a
   * reversal is a second correction better made by hand. Closed periods
   * INCLUDED: there the reversal is the only correction left.
   */
  const reversible =
    !readOnly &&
    !isNew &&
    entry !== null &&
    posted &&
    !closing &&
    entry.reversesHeaderId === null &&
    entry.reversedByHeaderId === null;

  if (loadError) {
    return (
      <section>
        <p role="alert" className="error">
          {loadError}
        </p>
        <button type="button" className="link" onClick={() => navigate('/triage')}>
          Back to the Queue
        </button>
      </section>
    );
  }

  /*
   * A new entry has no `entry` and no version and is not loading. The guard
   * used to mean "not ready"; it now means "not ready, unless there is nothing
   * to be ready for".
   */
  if (!entry) return <p className="muted">Loading…</p>;
  if (!isNew && version === null) return <p className="muted">Loading…</p>;

  return (
    <section className="editor">
      <header className="row triage-head">
        {/*
          What the BANK called it, read-only. counterparty is no longer edited
          here: it was doing two jobs -- the bank's descriptor and the
          operator's name for who this is -- and doing both badly, which is why
          editing it felt like losing something. The party below is the
          operator's answer; this is the transaction's own identity, and it is
          how you recognise the row you came from.

          A manual entry has no descriptor, so it falls back to the party.
        */}
        <h1>
          {isNew
            ? kind === 'pledge'
              ? 'New Pledge'
              : kind === 'invoice'
              ? 'New Invoice'
              : goodsKind !== null
              ? GOODS_TITLES[goodsKind]
              : 'New Journal Entry'
            : (entry.counterparty ??
              partyName ??
              (invoiceLike ? 'Invoice' : (goodsTitle(entry.lines) ?? 'Uncategorized Transaction')))}
        </h1>
        {/*
          §10 — walking the queue from inside the editor.

          Both buttons carry the SAME query string onward, so the snapshot the
          next screen takes is the same list. Dropping it would land the
          operator on an entry with no way back to the sequence they were in.

          Disabled rather than hidden at the ends. A control that disappears at
          the last entry moves the other one under the cursor, and the operator
          clicks the wrong thing on the row they were about to finish.
        */}
        {!isNew && (
        <div className="row queue-walk">
          <button
            type="button"
            className="link"
            disabled={previousId === undefined}
            title="Previous entry ( [ )"
            onClick={() => navigate(`/journals/${previousId}?${queueQuery}`)}
          >
            ‹ Previous
          </button>
          <button
            type="button"
            className="link"
            disabled={nextId === undefined}
            title="Next entry ( ] )"
            onClick={() => navigate(`/journals/${nextId}?${queueQuery}`)}
          >
            Next ›
          </button>
          <button
            type="button"
            className="link"
            onClick={() => navigate(`/triage${queueQuery ? `?${queueQuery}` : ''}`)}
          >
            Back to the Queue
          </button>
        </div>
        )}
        {isNew && (
          <button
            type="button"
            className="link"
            onClick={() => navigate('/triage')}
          >
            Cancel
          </button>
        )}
      </header>

      <dl className="facts">
        {/*
          §17 — THE DATE IS EDITABLE ONLY ON A NEW MANUAL ENTRY.

          A bank-fed header's date and counterparty are what the sync recorded,
          and the operator asked for them to stay that way. The invoice number
          is the exception, and only on a manual invoice: it is editable once
          saved (the operator's request, 2026-09-14) -- see just below.
          The date is the one that is arguable -- the sync's own code says it
          is operator-editable and deliberately never overwrites it -- but no
          screen has ever offered that edit, and widening it here was not what
          was asked for. Recorded rather than done.
        */}
        <div className="fact">
          <dt>{isNew ? <label htmlFor="entry-date">Entry Date</label> : 'Entry Date'}</dt>
          <dd>
            {isNew ? (
              <input
                id="entry-date"
                type="date"
                value={entryDate}
                onChange={(event) => setEntryDate(event.target.value)}
              />
            ) : (
              entry.entryDate
            )}
          </dd>
        </div>
        {/*
          Once the entry exists, its invoice number is SHOWN -- it was only ever
          an input on the new-invoice form, and the operator read eleven seeded
          invoices as missing their numbers (2026-09-13). And on an invoice it
          is EDITABLE (2026-09-14), saved with the entry. Text when the books
          are closed through its date, or on a bank row.
        */}
        {!isNew && invoiceLike && !locked && (
          <div className="fact">
            <dt>
              <label htmlFor="invoice-reference">{pledgeLike ? 'Pledge Number' : 'Invoice Number'}</label>
            </dt>
            <dd>
              <input
                id="invoice-reference"
                value={invoiceReference}
                onChange={(event) => setInvoiceReference(event.target.value)}
              />
            </dd>
          </div>
        )}
        {!isNew && (!invoiceLike || locked) && entry.invoiceReference && (
          <div className="fact">
            <dt>Invoice Number</dt>
            <dd>{entry.invoiceReference}</dd>
          </div>
        )}
        {/*
          §6.9 — WHEN IT IS DUE.

          The Invoices screen could record this and the editor could not even
          show it, so an operator who came here looking for a due date found
          nothing and reasonably concluded there was no way to set one. That is
          what this field is for.

          Empty means NO TERMS RECORDED, which migration 0036 insists is a
          different answer from "due today": the forecast omits an invoice with
          no due date rather than guessing one, so clearing this field takes the
          row off the forecast instead of moving it to today.
        */}
        {takesTerms && !locked && (
          <div className="fact">
            <dt>
              <label htmlFor="due-date">Due Date</label>
            </dt>
            <dd>
              <input
                id="due-date"
                type="date"
                value={dueDate}
                onChange={(event) => setDueDate(event.target.value)}
              />
              {dueDate === '' && (
                <span className="muted"> No terms recorded, so it is not on the forecast.</span>
              )}
            </dd>
          </div>
        )}
        {/*
          0084 (§B.17.3): an event gift records what the donor received in return,
          at its fair market value, so the year-end letter can say what was
          deductible (Publication 1771). Empty is nothing given in return.
        */}
        {eventGift && !locked && (
          <div className="fact">
            <dt>
              <label htmlFor="goods-services">Goods or Services Provided</label>
            </dt>
            <dd>
              <input
                id="goods-services"
                inputMode="decimal"
                value={goodsServices}
                onChange={(event) => {
                  const typed = event.target.value;
                  setGoodsServices(typed);
                }}
              />
              {goodsServices.trim() === '' && <span className="muted"> Nothing given in return.</span>}
            </dd>
          </div>
        )}
        {!isNew && (!takesTerms || locked) && entry.dueDate && (
          <div className="fact">
            <dt>Due Date</dt>
            <dd>{entry.dueDate}</dd>
          </div>
        )}
        {isNew && receivableKind && (
          /* Only for an invoice. On a general journal entry the field means
             nothing, which is the whole reason the menu asks first. */
          <div className="fact">
            <dt>
              <label htmlFor="invoice-reference">{pledgeLike ? 'Pledge Number' : 'Invoice Number'}</label>
            </dt>
            <dd>
              <input
                id="invoice-reference"
                value={invoiceReference}
                onChange={(event) => setInvoiceReference(event.target.value)}
              />
            </dd>
          </div>
        )}
        {/* §9.6 — each side of a reversal points at the other. */}
        {entry.reversesHeaderId && (
          <div className="fact">
            <dt>Reverses</dt>
            <dd>
              <Link to={`/journals/${entry.reversesHeaderId}`}>The Original Entry</Link>
            </dd>
          </div>
        )}
        {entry.reversedByHeaderId && (
          <div className="fact">
            <dt>Reversed By</dt>
            <dd>
              <Link to={`/journals/${entry.reversedByHeaderId}`}>The Reversal</Link>
            </dd>
          </div>
        )}
        <div className="fact">
          <dt>Bank Amount</dt>
          <dd>{entry.bankAmountCents === null ? '—' : formatCents(entry.bankAmountCents)}</dd>
        </div>
        {/*
          §9.1 — WHICH ACCOUNT the money moved on.
          
          The single most important fact about a bank-fed entry and it was not
          shown. Two headers of a card payment are the same date and the same
          amount and differ ONLY by this; the screen was asking the operator to
          tell them apart on information it did not display.

          It is also what the derived bank line below is computed from, so the
          screen was acting on a fact it kept to itself.

          "Account", not "Bank account": 2100 Credit Card carries the bank role
          and is not a bank account in the sense anybody reading this means.
        */}
        <div className="fact">
          <dt>Account</dt>
          <dd>
            {bankReading
              ? `${bankReading.account.code} ${bankReading.account.name}`
              : '—'}
          </dd>
        </div>
        <div className="fact">
          <dt>Status</dt>
          <dd>{statusLabel({ status: entry.status, lineCount: entry.lines.length })}</dd>
        </div>
        {/*
          §9.1 — how the money moved, as the bank reported it (migration 0023).

          "—" for null, and null means NOT CAPTURED rather than "no rail": every
          manual entry, and the posted headers that predate 0023 and can never
          be backfilled, because the bank's facts on a posted header are still
          refused after migration 0027 made the rest of it editable.

          It does NOT reliably separate ACH from check. The largest value in
          production is `other` and nothing in Mercury's set names ACH, so this
          is shown as a fact the bank stated rather than labelled as a rail.

          THIS REPLACED "Version", WHICH DID NOT BELONG ON A SCREEN. The version
          is §9.4's concurrency token: the editor holds it, sends it back on
          every write, and a trigger owns the increment. "Version 11" tells an
          operator nothing they can act on, and a number nobody can act on in a
          list of facts they must read teaches them to skim the list.
        */}
        <div className="fact">
          <dt>Method</dt>
          {/* `title` keeps the RAW value reachable. The column stores what the
              bank said, verbatim (0023), and "Credit Card Transaction" is not
              a string anyone can quote back to Mercury support. */}
          <dd title={entry.mercuryKind ?? undefined}>{kindLabel(entry.mercuryKind)}</dd>
        </div>
        {/*
          §9.1 and migration 0026 — the bank's own words, once they are no
          longer what the Memo field says.

          The Memo field below is EDITABLE and is now the operator's alone: the
          sync seeds it when the row is created and never writes it again. That
          is what makes this row necessary. Before 0026 those were one column,
          so retyping a memo destroyed the descriptor Mercury sent, and §9.2
          builds the embedding text from that descriptor.

          Hidden while the two match, which is every entry nobody has retyped.
          A fact list that repeats the field two rows below it on almost every
          screen is a fact list people stop reading.
        */}
        {entry.bankMemo !== null && entry.bankMemo !== memo && (
          <div className="fact">
            <dt>Bank Memo</dt>
            <dd>{entry.bankMemo}</dd>
          </div>
        )}
      </dl>

      {/*
        First field, not last. Buried at the bottom next to the memo it would
        be skipped on every entry; the party is the thing this screen is for
        after the lines themselves.
      */}
      {/*
        §9.1 — one movement, reported twice.

        Paying the credit card happens on two accounts and Mercury reports each
        account's view, so it arrives as two headers of equal and opposite
        amounts on the same day. It is ONE journal entry.

        WHAT IT SAYS DEPENDS ENTIRELY ON THE OTHER SIDE'S STATUS, and the posted
        case is the one that earns this. A posted counterpart means the movement
        is already in the books and THIS header is the duplicate -- posting it
        too would record the same money twice, which no trigger catches because
        both entries balance and both net their own bank amount.

        Never automatic. The match is inferred from a coincidence of date,
        amount and account, and a wrong automatic dismissal would hide a real
        transaction with nothing anywhere to flag it.
      */}
      {!posted && counterpart && (
        <p className="notice transfer-note" role="status">
          {/*
            PLAIN WORDS. The first version said "one movement reported twice",
            which is the bookkeeping abstraction rather than what the operator
            is looking at. What they see is two bank transactions and one
            journal entry, so that is what it says.

            The posted sentence names the CONSEQUENCE rather than the state.
            "It is posted" is a fact about the other entry; "posting this too
            would record $30.27 twice" is the reason to care, and it is the
            failure no trigger catches.
          */}
          {counterpart.status === 'posted'
            ? `Already recorded. The other transaction is posted on ${counterpart.accountCode} ${counterpart.accountName}. Posting this one too would record ${formatCents(Math.abs(counterpart.bankAmountCents))} twice.`
            : `This journal entry may have two transactions. ${counterpart.accountCode} ${counterpart.accountName} carries the other one on the same day.`}{' '}
          <a href={`/journals/${counterpart.headerId}${queueQuery ? `?${queueQuery}` : ''}`}>
            Open It
          </a>
          {' · '}
          {!readOnly && (
            <button type="button" className="link" onClick={() => void excludeAsTransfer()}>
              Exclude This One
            </button>
          )}
        </p>
      )}

      <div className="row party-row">
        <label className="party-field">
          <span className="party-label">
            Party
            {/*
              §7 — WHY this field filled itself, behind a disclosure.

              The sentence used to sit under the field on every filled entry,
              and on a screen that already carries a status line, an offer and
              a balance rail it was one more line to read past. It is still
              required -- a fill an operator cannot judge is one they stop
              reading -- but it is an explanation, wanted once, not standing
              information wanted always.

              A BUTTON, not a hover tooltip, for the reason the bank-amount
              note gives twenty lines below: hover has no touch equivalent and
              no keyboard one.

              Conditional, so choosing a party by hand shows no icon at all.
              There is nothing to explain when the operator did it themselves.
            */}
            {!locked && partyMatch !== null && (
              <button
                type="button"
                className="info-dot"
                aria-expanded={partyNoteOpen}
                aria-controls="party-note"
                aria-label={
                  partyNoteOpen ? 'Hide why this party filled in' : 'Why this party filled in'
                }
                onClick={(event) => {
                  // The label wraps the picker, so a click here would otherwise
                  // focus and open the combobox as well.
                  event.preventDefault();
                  setPartyNoteOpen((open) => !open);
                }}
              >
                i
              </button>
            )}
          </span>
          <div className="party-control">
            <Combobox
            label="Party"
            value={partyId}
            options={parties.map((party) => ({ id: party.id, label: party.name }))}
            disabled={locked}
            placeholder="Search or add a party…"
            onChange={(chosen) => {
              setPartyId(chosen);
              // Chosen by hand, so the fill's provenance no longer applies.
              setPartyMatch(null);
              setSavedAlias(null);
              setAliasDone(false);
            }}
            onCreate={beginCreateParty}
            createLabel={(name) => `Add “${name}” as a new party`}
            /*
              The bank's own descriptor, offered when nothing has been typed.
              Opening the picker on an uncoded transaction should lead with the
              obvious candidate -- the name already on the screen -- rather than
              an empty list and a cursor. The create form still opens with the
              name editable, so a raw "HOMEDEPOT #4412 COLUMBUS OH" gets cleaned
              up before it becomes a party.
            */
            suggestedCreateName={entry.counterparty ?? undefined}
            />
            {/*
              §7 — the roles the chosen party holds.

              INSIDE the field's control row, not beside the field in the outer
              row, and that is the fix rather than a restyle.

              The previous attempt bottom-aligned a badge in the outer row
              against a COLUMN containing a label and an input, then matched its
              padding and border to the input so the two boxes would come out
              the same height. It stayed misaligned, and a second guess at why
              would be the third time this project argued a CSS diagnosis from a
              screenshot. The dropdown bug was only fixed when the fix stopped
              depending on the diagnosis being right.

              So the badge is now a sibling of the picker on one centred line.
              Two items on the same line box centre against each other whatever
              their heights are, and there is no outer-row cross-axis question
              left to get wrong.
            */}
            {partyRoles !== null && !draftParty && (
              <span className="tag party-role">{partyRoles}</span>
            )}
            {partyRoles === null && party && !draftParty && (
              /* Not a badge. See roleLabels: chrome round "no role set" would
                 assert a value where there is not one. */
              <span className="muted party-role-none">No role set</span>
            )}
          </div>
        </label>
      </div>

      {/*
        0085 (§B.18.5): a private foundation's disqualified person. Flagged,
        never refused: some payments to one are permitted, which is the CPA's
        call, so the entry saves and posts as any other.
      */}
      {party?.isDisqualifiedPerson === true && party.disqualifiedRelationship && (
        <p className="notice notice-warn" role="note">
          {party.name} is a disqualified person ({RELATIONSHIP_LABELS[party.disqualifiedRelationship].toLowerCase()}). A
          payment to them may be self-dealing; your CPA decides.
        </p>
      )}

      {/*
        §7 — where the party came from, and the alias worth keeping.

        PROVENANCE FIRST. §9.7 point 6 requires it of a suggestion, and the
        argument is the same for a fill: an operator who cannot see WHY a field
        filled in cannot judge whether it is right, and a fill they cannot judge
        is one they stop reading. This says which alias matched, by name.

        THE OFFER IS THE POINT. Production has one customer under three
        spellings; the alias that fixes it can only be learned where the mapping
        is being made, which is here. A settings screen nobody visits stays
        empty, and the operator would go on picking the party by hand forever.
      */}
      {!locked && partyMatch !== null && partyNoteOpen && (
        <p className="muted party-provenance" id="party-note">
          {partyMatch.matchedOn === 'alias'
            ? `Filled from the alias “${partyMatch.matchedAlias}”.`
            : `Filled because the counterparty is this party's name.`}{' '}
          Not saved until you save the entry.
        </p>
      )}
      {!locked && aliasOffer !== null && savedAlias === null && !aliasDone && (
        <p className="muted party-provenance">
          Also file “{aliasOffer}” under {parties.find((p) => p.id === partyId)?.name}?{' '}
          <button type="button" className="link" onClick={() => void saveAlias()}>
            Remember It
          </button>
        </p>
      )}
      {savedAlias && (
        <Toast
          message={`Saved. “${savedAlias.alias}” will fill this party from now on.`}
          variant="ok"
          action={{ label: 'Undo', onAction: () => void undoAlias() }}
          onDismiss={() => setSavedAlias(null)}
        />
      )}

      {/*
        §9.7 steps 1 and 6 — the rule suggestion.

        PROVENANCE IS THE WHOLE SENTENCE, shown exactly as the server built it:
        `rule: "AWS" → 6100 Software`. §9.7 step 6 requires a suggestion to say
        where it came from, and FINDINGS.md names accept-by-reflex as the thing
        that makes such a feature worthless. An account code appearing with no
        reason attached is precisely what trains it.

        NOTHING IS PRE-FILLED. The button is the entire mechanism. When every
        line already has an account there is nothing to fill, so the suggestion
        is shown without an action rather than offering to overwrite a choice
        the operator already made.
      */}
      {!posted && ruleSuggestion !== null && (
        <p className="muted party-provenance">
          {ruleSuggestion.provenance}
          {lineAwaitingAccount ? (
            <>
              {' '}
              <button type="button" className="link" onClick={applyRule}>
                Use It
              </button>
            </>
          ) : (
            <> Every line already has an account.</>
          )}
        </p>
      )}

      {/*
        The capture point. Same argument as the alias offer above it: this is
        the moment the operator has made the mapping, and a rules screen they
        never open would stay empty.
      */}
      {ruleWorthOffering !== null && (
        <p className="muted party-provenance">
          Always categorize “{ruleWorthOffering.counterparty}” as{' '}
          {accounts.find((a) => a.id === ruleWorthOffering.accountId)?.code}?{' '}
          <button type="button" className="link" onClick={() => void saveRule()}>
            Make It a Rule
          </button>
        </p>
      )}
      {savedRule && (
        <Toast
          message={`Saved. “${savedRule.pattern}” will suggest that account from now on.`}
          variant="ok"
          action={{ label: 'Undo', onAction: () => void undoRule() }}
          onDismiss={() => setSavedRule(null)}
        />
      )}

      {draftParty && (
        <div className="new-party" role="group" aria-label="New party">
          <label className="party-field">
            <span>Name</span>
            <input
              aria-label="New party name"
              value={draftParty.name}
              onChange={(event) =>
                setDraftParty({ ...draftParty, name: event.target.value })
              }
            />
          </label>

          {/*
            Checked from how the entry is being coded, and visible. A default
            the operator can see and change is a suggestion; the same value
            applied without a form was the system classifying for them.
          */}
          <fieldset className="party-roles">
            <legend>What Are They?</legend>
            {([
              ['isCustomer', 'Customer'],
              ['isVendor', 'Vendor'],
              ['isEmployee', 'Employee'],
              ['isCharity', 'Charity'],
            ] as const).map(([key, caption]) => (
              <label key={key}>
                <input
                  type="checkbox"
                  checked={draftParty[key] === true}
                  onChange={(event) =>
                    setDraftParty({ ...draftParty, [key]: event.target.checked })
                  }
                />{' '}
                {caption}
              </label>
            ))}
          </fieldset>

          <div className="row">
            <button
              type="button"
              disabled={busy || draftParty.name.trim() === ''}
              onClick={() => void commitCreateParty()}
            >
              Add Party
            </button>
            <button type="button" className="link" onClick={() => setDraftParty(null)}>
              Cancel
            </button>
          </div>
          {/*
            No role is required. An employee who is also a customer is
            expressible, and so is a party whose role nobody has decided yet --
            forcing a choice here would just get a wrong one clicked to move on.
          */}
        </div>
      )}

      {/*
        §6.9 — the one read-only state left, said once.

        Only a closed period makes an entry fixed since migration 0027, so this
        names the date that did it and where to undo that. A posted entry in an
        open period carries no banner: the Status fact above says Posted, and a
        standing notice on an entry that behaves like any other would be a line
        read past on every open.
      */}
      {readOnly && !isNew && (
        <p role="status" className="notice notice-posted">
          Your access to this business lets you read this entry, not change it. An admin can change
          that in Settings, People.
        </p>
      )}
      {/* Round 73's SW-2: /journals/new typed by hand showed the withheld form with no sentence. */}
      {readOnly && isNew && (
        <p role="status" className="notice notice-posted">
          Your access to this business does not let you make an entry. An admin can change that in
          Settings, People.
        </p>
      )}
      {!readOnly && locked && closing && (
        <p role="status" className="notice notice-posted">
          This entry closes fiscal {entry!.closesFiscalYear} into retained earnings and is never
          edited. To undo it, reopen the year in <Link to={settingsTab('books')}>Settings</Link>.
        </p>
      )}
      {!readOnly && locked && !closing && (
        <p role="status" className="notice notice-posted">
          The books are closed through {lockedThrough}, so this entry cannot be changed. Reopen
          them in <Link to={settingsTab('books')}>Settings</Link>.
        </p>
      )}
      {notice && !saveError && (
        // No full stop on these. They are status labels -- "Saved", "Added
        // Mercury Bank" -- not sentences, and a trailing period on a two-word
        // confirmation reads as clipped prose. Server messages are left exactly
        // as sent: those ARE sentences, written for the operator by §5.
        //
        // A TOAST, because every one of these is an event that just happened
        // because the operator acted. The notice directly above -- "this entry
        // is posted" -- is a standing fact about the entry and stays put. That
        // is the whole line between the two controls.
        <Toast message={notice} variant="ok" onDismiss={() => setNotice(null)} />
      )}
      {saveError && (
        <p role="alert" className="error">
          {saveError}
        </p>
      )}

      <table className="lines editor-lines">
        <thead>
          <tr>
            {/*
              The handle column, headerless on purpose: "Reorder" over a column
              of grip dots is a label for a control that already says what it
              is, and it would be read out before every account name.
            */}
            {!locked && <th className="line-grip-head" aria-hidden="true" />}
            <th>Account</th>
            {usesFunds && <th>Fund</th>}
            {showProjects && <th>{projectLabel}</th>}
            {showPhases && <th>Phase</th>}
            {showLabels && <th>Labels</th>}
            <th className="num">Debit</th>
            <th className="num">Credit</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => (
            <tr
              key={line.key}
              className={dragging === index ? 'is-dragging' : undefined}
              // On the ROW, not on the handle: the drop target is the whole
              // row, or a drop would only land on a few pixels of grip.
              onDragOver={(event) => {
                if (dragging === null) return;
                event.preventDefault();
                if (dragging !== index) {
                  moveLine(dragging, index);
                  setDragging(index);
                }
              }}
              onDrop={(event) => {
                if (dragging === null) return;
                event.preventDefault();
                setDragging(null);
              }}
            >
              {!locked && (
                <td className="line-grip-cell">
                  <button
                    type="button"
                    className="line-grip"
                    draggable
                    /*
                     * Named by position and by what it does, because a column
                     * of controls all called "Reorder" is unusable by ear. The
                     * account is not in the name: it changes as the operator
                     * types, and a handle renaming itself mid-edit is noise.
                     */
                    aria-label={`Move line ${index + 1}. Alt with an arrow key moves it.`}
                    title="Drag to reorder, or Alt with an arrow key"
                    onDragStart={(event) => {
                      setDragging(index);
                      event.dataTransfer.effectAllowed = 'move';
                      // Firefox starts no drag at all unless something is set.
                      event.dataTransfer.setData('text/plain', String(index));
                    }}
                    onDragEnd={() => setDragging(null)}
                    onKeyDown={(event) => {
                      if (!event.altKey) return;
                      const to =
                        event.key === 'ArrowUp'
                          ? index - 1
                          : event.key === 'ArrowDown'
                            ? index + 1
                            : null;
                      if (to === null) return;
                      // Alt+Arrow is a browser history shortcut in some
                      // builds; a line that moved and a page that went back
                      // would be the same keystroke.
                      event.preventDefault();
                      if (to < 0 || to >= lines.length) return;
                      moveLine(index, to);
                      /*
                       * Focus follows the line, so a second Alt+Arrow moves
                       * the same line again. Without this the focused handle
                       * keeps its index and the operator ends up swapping two
                       * lines back and forth -- the commonest way a keyboard
                       * reorder is got wrong. Deferred because the row this
                       * lands on has not rendered yet.
                       */
                      const grips = event.currentTarget.closest('tbody');
                      queueMicrotask(() => {
                        const moved = grips?.querySelectorAll<HTMLButtonElement>('.line-grip')[to];
                        moved?.focus();
                      });
                    }}
                  >
                    <span aria-hidden="true">⠿</span>
                  </button>
                </td>
              )}
              <td>
                {/*
                  Grouped by the party's role, never preselected. §9.7 and
                  FINDINGS.md: a field that is right most of the time stops
                  being read, so the likely accounts are gathered under a
                  heading rather than promoted to the top of a flat list.
                */}
                <Combobox
                  label={`Account for line ${index + 1}`}
                  value={line.accountId}
                  options={accountOptions}
                  groupOrder={accountGroupOrder}
                  disabled={locked || goodsFixed(line)}
                  placeholder="Search accounts…"
                  onChange={(accountId) => update(line.key, { accountId })}
                  onCreate={(name) => setDraftAccount({ lineKey: line.key, name })}
                />
                {/*
                  0086 (§14.7): the goods a stock line moves, or a sale's revenue
                  line sells. A stock credit is priced from what it takes, so its
                  amount is the preview's, and it names the expense line that is
                  its cost.
                */}
                {namesGoods(line) && (
                  <span className="line-goods">
                    <select
                      aria-label={`Item for line ${index + 1}`}
                      value={line.itemId}
                      disabled={locked || goodsFixed(line) || line.fromKey !== null}
                      onChange={(event) => {
                        const itemId = event.target.value;
                        const chosen = items.find((candidate) => candidate.id === itemId);
                        update(line.key, {
                          itemId,
                          ...(line.accountId === '' && chosen?.defaultRevenueAccountId ? { accountId: chosen.defaultRevenueAccountId } : {}),
                        });
                      }}
                    >
                      <option value="">{items.length === 0 ? 'No Items Yet' : 'Choose An Item'}</option>
                      {items.map((candidate) => (
                        <option key={candidate.id} value={candidate.id}>
                          {candidate.name}
                        </option>
                      ))}
                      {line.itemId !== '' && !items.some((candidate) => candidate.id === line.itemId) && (
                        <option value={line.itemId}>Archived Item</option>
                      )}
                    </select>
                    <input
                      aria-label={`Quantity for line ${index + 1}`}
                      className="line-quantity"
                      inputMode="decimal"
                      value={line.quantity}
                      disabled={locked || goodsFixed(line) || line.fromKey !== null}
                      onChange={(event) => {
                        const quantity = event.target.value;
                        update(line.key, { quantity });
                      }}
                    />
                    <span className="muted">{items.find((candidate) => candidate.id === line.itemId)?.unit ?? ''}</span>
                    {isOutLine(line) && <span className="muted">{posted ? '· Priced At Posting' : '· Priced When Posted'}</span>}
                    {drawnFixed(line) && (
                      <span className="muted">· Drawn on, so fixed; reverse the entry to correct it.</span>
                    )}
                    {saleFixed(line) && <span className="muted">· Sold, so fixed; reverse the sale to change what it sold.</span>}
                    {movedInto(line) !== undefined && (
                      <span className="muted">· Moves Into Line {lines.indexOf(movedInto(line)!) + 1}</span>
                    )}
                    {movedFrom(line) !== undefined && (
                      <span className="muted">· Moved From Line {lines.indexOf(movedFrom(line)!) + 1}</span>
                    )}
                    {pricedHere(line) && !locked && movedInto(line) === undefined && (
                      <select
                        aria-label={`Cost line for line ${index + 1}`}
                        value={lines.find((other) => other.costsKey === line.key)?.key ?? ''}
                        onChange={(event) => {
                          const chosen = event.target.value;
                          setLines((current) =>
                            current.map((other) =>
                              other.key === chosen
                                ? { ...other, costsKey: line.key }
                                : other.costsKey === line.key
                                  ? { ...other, costsKey: null }
                                  : other,
                            ),
                          );
                        }}
                      >
                        <option value="">Choose Its Cost Line</option>
                        {lines
                          .filter((other) => other.key !== line.key && isExpenseLine(other))
                          .map((other) => (
                            <option key={other.key} value={other.key}>
                              Cost On Line {lines.indexOf(other) + 1}
                            </option>
                          ))}
                      </select>
                    )}
                    {goodsErrors[line.key] && (
                      <span role="alert" className="error line-goods-error">
                        {goodsErrors[line.key]}
                      </span>
                    )}
                  </span>
                )}
              </td>
              {usesFunds && (
                <td className="line-fund">
                  <select
                    aria-label={`Fund for line ${index + 1}`}
                    value={line.fundId === '' ? (defaultFund?.id ?? '') : line.fundId}
                    disabled={locked || drawnFixed(line)}
                    onChange={(event) => {
                      const fundId = event.target.value;
                      update(line.key, { fundId });
                    }}
                  >
                    {funds
                      // An archived fund takes no new line, and is offered
                      // only to the line already in it.
                      .filter((fund) => fund.archivedAt === null || fund.id === line.fundId)
                      .map((fund) => (
                        <option key={fund.id} value={fund.id}>
                          {fund.code} {fund.name}
                          {fund.archivedAt === null ? '' : ' (Archived)'}
                        </option>
                      ))}
                  </select>
                </td>
              )}
              {showProjects && (
                <td className="line-project">
                  {carriesProject(line.accountId) ? (
                    <select
                      aria-label={`${projectLabel} for line ${index + 1}`}
                      value={
                        line.projectId === null
                          ? ''
                          : line.projectId !== ''
                            ? line.projectId
                            : nonprofit && isExpense(line.accountId)
                              ? (defaultProject?.id ?? '')
                              : ''
                      }
                      disabled={locked || drawnFixed(line)}
                      onChange={(event) => {
                        const chosen = event.target.value;
                        update(line.key, { projectId: chosen === '' ? null : chosen });
                      }}
                    >
                      {/* A non-profit's expense line always names a program (0081). */}
                      {!(nonprofit && isExpense(line.accountId)) && <option value="">None</option>}
                      {projects
                        .filter((project) => project.archivedAt === null || project.id === line.projectId)
                        .map((project) => (
                          <option key={project.id} value={project.id}>
                            {'\u2003'.repeat(Math.max(0, (project.depth ?? (project.parentId === null ? 1 : 2)) - 1))}
                            {project.name}
                            {project.archivedAt === null ? '' : ' (Archived)'}
                          </option>
                        ))}
                    </select>
                  ) : null}
                </td>
              )}
              {showPhases && (
                <td className="line-phase">
                  {carriesPhase(line.accountId) ? (
                    <select
                      aria-label={`Phase for line ${index + 1}`}
                      value={line.phaseId ?? ''}
                      disabled={locked || drawnFixed(line)}
                      onChange={(event) => {
                        const chosen = event.target.value;
                        update(line.key, { phaseId: chosen === '' ? null : chosen });
                      }}
                    >
                      <option value="">None</option>
                      {phases
                        .filter((phase) => phase.archivedAt === null || phase.id === line.phaseId)
                        .map((phase) => (
                          <option key={phase.id} value={phase.id}>
                            {'\u2003'.repeat(Math.max(0, (phase.depth ?? 1) - 1))}
                            {phase.name}
                            {phase.archivedAt === null ? '' : ' (Archived)'}
                          </option>
                        ))}
                    </select>
                  ) : null}
                </td>
              )}
              {showLabels && (
                <td className="line-labels">
                  {line.labelIds.map((labelId) => {
                    const label = labelInfo.get(labelId);
                    return (
                      <LabelChip
                        key={labelId}
                        name={label?.name ?? 'Label'}
                        colour={label?.colour ?? 'grey'}
                        archived={label?.archived}
                        {...(!locked && {
                          removeLabel: `Remove ${label?.name ?? 'the label'} from line ${index + 1}`,
                          onRemove: () => update(line.key, { labelIds: line.labelIds.filter((id) => id !== labelId) }),
                        })}
                      />
                    );
                  })}
                  {!locked && labelYear !== null && (
                    <Combobox
                      label={`Add a label to line ${index + 1}`}
                      value=""
                      placeholder="Add label"
                      options={labelYear.labels
                        .filter((label) => label.archivedAt === null && !line.labelIds.includes(label.id))
                        .map((label) => ({ id: label.id, label: label.name, ...(label.group !== null && { group: label.group }) }))}
                      onChange={(labelId) => update(line.key, { labelIds: [...line.labelIds, labelId] })}
                      {...(mayEditLabels && {
                        createLabel: (name: string) => `New label "${name}" in fiscal ${labelYear.fiscalYear}`,
                        onCreate: (name: string) => void addLabel(line.key, name),
                      })}
                    />
                  )}
                </td>
              )}
              <td className="num">
                <input
                  aria-label={`Debit for line ${index + 1}`}
                  inputMode="decimal"
                  value={line.debit}
                  disabled={locked || drawnFixed(line)}
                  // 0086: a cost line's amount is what its goods took, never typed; 0088: a transfer's in-line's too.
                  readOnly={keepsGoods && (line.costsKey !== null || line.fromKey !== null)}
                  onChange={(event) =>
                    // A line carries a debit or a credit, never both. Typing in
                    // one clears the other rather than letting the server
                    // reject the pair later.
                    update(line.key, { debit: event.target.value, credit: '' })
                  }
                  // On blur, not on keystroke: rewriting the field while
                  // someone is still typing moves the cursor out from under
                  // them. Here it also confirms what was understood -- ".45"
                  // becoming "0.45" is the app agreeing with you.
                  onBlur={() => update(line.key, { debit: normaliseAmountInput(line.debit) })}
                />
              </td>
              <td className="num">
                <input
                  aria-label={`Credit for line ${index + 1}`}
                  inputMode="decimal"
                  value={line.credit}
                  disabled={locked || drawnFixed(line)}
                  // 0086: goods leaving are priced by what they take (§14.5).
                  readOnly={keepsGoods && isOutLine(line)}
                  onChange={(event) =>
                    update(line.key, { credit: event.target.value, debit: '' })
                  }
                  onBlur={() => update(line.key, { credit: normaliseAmountInput(line.credit) })}
                />
              </td>
              {/*
                Remove FIRST, Fill second, and the order is the point.
                Fill appears on some rows and not others; with Fill first it
                shifted Remove sideways row by row, so the one destructive
                control on the screen sat at a different place depending on
                whether the line above it happened to be codeable. A control
                that deletes something should not move. Fill grows to its
                right, where an appearing button is its own signal.
              */}
              <td className="line-actions">
                {!locked && !drawnFixed(line) && !saleFixed(line) && !keepsItsLine(line) && (
                  <button
                    type="button"
                    className="link"
                    disabled={lines.length <= 1}
                    aria-label={`Remove line ${index + 1}`}
                    onClick={() =>
                      setLines((current) =>
                        current.filter((candidate) => candidate.key !== line.key),
                      )
                    }
                  >
                    Remove
                  </button>
                )}
                {(() => {
                  const fill = fillFor(line);
                  if (!fill) return null;
                  return (
                    <button
                      type="button"
                      className="link"
                      aria-label={`Fill line ${index + 1} ${fill.side} with ${formatCents(fill.cents)}`}
                      onClick={() =>
                        update(line.key, {
                          [fill.side]: formatAmountInput(fill.cents),
                          [fill.side === 'debit' ? 'credit' : 'debit']: '',
                        })
                      }
                    >
                      Fill {formatCents(fill.cents)}
                    </button>
                  );
                })()}
              </td>
            </tr>
          ))}
          {interfundLines.map((line) => (
            <tr key={line.id} className="interfund-line">
              {!locked && <td />}
              <td>
                {line.accountCode} {line.accountName}
              </td>
              <td>{line.fundCode}</td>
              {showProjects && <td />}
              {showPhases && <td />}
              {showLabels && <td />}
              {/* Written as the fields above them are, so the columns read as one. */}
              <td className="num">{line.debitCents > 0 ? formatAmountInput(line.debitCents) : ''}</td>
              <td className="num">{line.creditCents > 0 ? formatAmountInput(line.creditCents) : ''}</td>
              <td />
            </tr>
          ))}
        </tbody>
      </table>

      {/*
        0079: the lines between funds are the save's. Said under the table,
        where they are, and only when there are some or a save will add them.
      */}
      {usesFunds && interfundLines.length > 0 && (
        <p className="muted">
          The last {interfundLines.length} lines are between funds: one fund paid for another, so the
          save records what each owes. They follow the lines above and are rewritten on every save.
        </p>
      )}
      {/* Before the first save that writes them; afterwards the sentence above says it. */}
      {usesFunds && balanced && fundsOwing.length > 0 && interfundLines.length === 0 && (
        <p className="muted" role="status">
          {fundsOwing.join(' and ')} do not each balance, so saving adds lines between them on the
          interfund account.
        </p>
      )}

      {/*
        Directly under the table, not inside the row that asked.

        A form inside a <td> inherits the table's column widths and has to be
        laid out twice -- once for the cell and once for itself. Below it, the
        form is the same component the accounts screen uses, and the row it
        belongs to is the one whose picker is still open behind it.
      */}
      {draftAccount && (
        <NewAccountForm
          busy={busy}
          initialName={draftAccount.name}
          onCancel={() => setDraftAccount(null)}
          onCreate={(body) => void commitCreateAccount(body)}
        />
      )}

      {/*
        Absent when locked rather than disabled. A disabled control still says
        "this is a thing you do here", and in a closed period it is not -- the
        notice above already says why. The same reasoning removes Remove from
        each row.
      */}
      {!locked && (
        <button
          type="button"
          className="link"
          onClick={() => setLines((current) => [...current, newLine()])}
        >
          Add a Line
        </button>
      )}
      {/* 0086 (§14.7): an invoice's line selling an item; posting records the goods' cost beside it. */}
      {/* Not on a posted invoice: its goods are what its cost entry took (0086's IA-1), so it is reversed instead. */}
      {!locked && !posted && keepsGoods && kind === 'invoice' && items.length > 0 && (
        <button
          type="button"
          className="link"
          onClick={() => {
            const first = items[0];
            setLines((current) => [
              ...current,
              { ...newLine(), itemId: first?.id ?? '', accountId: first?.defaultRevenueAccountId ?? '', quantity: '1' },
            ]);
          }}
        >
          Add Item
        </button>
      )}

      {/* 0088 (§15.10): goods back from a posted sale, or back to the vendor from a posted bill. */}
      {posted && !locked && keepsGoods && <GoodsReturnForm entryId={entry.id} />}

      {/*
        The balance rail. §9.4 refuses an unbalanced entry with a message in
        dollars; this says the same thing before the operator submits, so the
        difference is visible while it is still being typed.
      */}
      {/*
        The rail appears only when something is WRONG.
        It used to be permanent: "Debits $0.45 Credits $0.45 Balanced" sat
        under every finished entry, and "Nothing categorized yet" under every
        untouched one. Neither told the operator anything they could act on,
        and both competed for attention with the line that could -- the
        bank-amount mismatch directly below it.
        Balance is already signalled where it matters: Save and Post is
        disabled until the entry balances, and says why. A green confirmation
        beside an enabled button is the same fact twice.
      */}
      {railVisible && (
      <div className="rail" role="status" aria-label="Balance">
        <span>Debits {formatCents(totals.debits)}</span>
        <span>Credits {formatCents(totals.credits)}</span>
        {totals.invalid ? (
          <strong className="rail-off">An amount is not a number.</strong>
        ) : incompleteLines.length > 0 ? (
          <strong className="rail-off">
            Line {incompleteLines[0]!.number} needs an account.
          </strong>
        ) : !hasAmounts ? (
          // An entry nobody has touched is EMPTY, not out of balance. Zero
          // debits against zero credits differ by zero, so the arithmetic
          // branch produced "Out of balance by $0.00" -- a sentence that
          // states a discrepancy and then says there isn't one. It also
          // coloured a fresh entry as an error before the operator had done
          // anything wrong.
          <strong className="rail-idle">Nothing categorized yet.</strong>
        ) : !balanced ? (
          <strong className="rail-off">
            Out of balance by {formatCents(Math.abs(totals.difference))}
          </strong>
        ) : (
          /*
           * Balanced, and the rail is only on screen at all because the bank
           * line does not match. "Balanced" alone would be true and useless
           * here -- it is the reason the entry LOOKS ready.
           */
          <strong className="rail-off">The bank line does not match.</strong>
        )}

        {/*
          §9.3's detail, collapsed.
          Two red lines saying the same thing in different words was too much
          for a screen whose whole job is one number, and the second line is
          the one an operator reads once and then never again. It is still
          exact -- which account, what it must net, what it nets now -- so it
          is a click away rather than gone.

          A disclosure button, not a hover tooltip: hover has no touch
          equivalent and no keyboard one, and this is the sentence that
          explains why a save is about to fail.
        */}
        {bankMismatch && (
          <button
            type="button"
            className="info-dot"
            aria-expanded={bankNoteOpen}
            aria-controls="bank-note"
            aria-label={
              bankNoteOpen ? 'Hide why this will be refused' : 'Why this will be refused'
            }
            onClick={() => setBankNoteOpen((open) => !open)}
          >
            i
          </button>
        )}
      </div>
      )}

      {/*
        Only when it does not match, AND only once the operator has entered
        something. Two separate mistakes, fixed one at a time: first the line
        was shown when the coding was already correct ("must net $0.45;
        currently $0.45"), and then it still greeted an untouched entry with
        "the server will refuse this" before anyone had typed a character.
        An empty form is not a wrong one -- the same rule the balance rail
        needed, applied to the line beside it.
      */}
      {bankMismatch && bankNoteOpen && (
        <p className="muted bank-reading" id="bank-note">
          {bankReading!.account.code} {bankReading!.account.name} must net{' '}
          {formatCents(bankReading!.target)}; currently {formatCents(bankReading!.net)}. The
          server will refuse this.
        </p>
      )}

      {/*
        §6.3 — the payment matches on this entry, and undoing one in place.

        Shown before any save, so a line that cannot change says so before the
        operator tries, and refilled from `detail.matches` when a save is
        refused. Each row is phrased from THIS entry's side: which line, matched
        to which entry, for how much.

        Undo asks for a reason, as the Matching screen does, because the server
        requires one and it is the audit trail. No Undo on a locked entry: the
        notice at the top says why nothing here can change.
      */}
      {!isNew && matches.length > 0 && (
        <section className="entry-matches" aria-label="Payment Matches">
          <h2>Payment Matches</h2>
          <p className="muted">
            A matched line keeps its account and amount until its match is undone.
          </p>
          <ul className="match-list">
            {matches.map((match) => (
              <li key={match.clearingId}>
                <span>
                  Line {match.lineNumber} · {match.otherEntryDate}{' '}
                  {match.otherCounterparty ?? 'No counterparty'}
                  {match.otherInvoiceReference ? ` · ${match.otherInvoiceReference}` : ''}
                </span>
                <span className="num">{formatCents(match.amountCents)}</span>
                {!locked && (
                  <button
                    type="button"
                    className="link"
                    aria-label={`Undo Match on Line ${match.lineNumber} for ${formatCents(match.amountCents)}`}
                    onClick={() => setUndoing({ clearingId: match.clearingId, reason: '' })}
                  >
                    Undo Match
                  </button>
                )}
              </li>
            ))}
          </ul>
          {undoing !== null && (
            <div className="new-account undo-form">
              <label className="field">
                <span>Reason</span>
                <input
                  aria-label="Reason for undoing the match"
                  value={undoing.reason}
                  onChange={(event) => setUndoing({ ...undoing, reason: event.target.value })}
                />
              </label>
              <div className="row">
                <button
                  type="button"
                  disabled={busy || undoing.reason.trim() === ''}
                  onClick={() => void undoMatch()}
                >
                  Undo the Match
                </button>
                <button type="button" className="link" onClick={() => setUndoing(null)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {/*
        Receipts. Attaching does not touch the header, so an entry can gain a
        receipt at any time. Removing one from a posted entry is allowed while
        its period is open and recorded in its History (0027); `deletable` is
        the server's answer and the screen does not second-guess it.
      */}
      <section className="receipts" aria-label="Receipts">
        <h2>Receipts</h2>

        {/*
          §17 — attaching to a new entry SAVES it first.

          A receipt is stored against a header id and a new entry has none, so
          dropping a file creates the entry and then attaches to it. Said out
          loud rather than done quietly: the operator is about to find
          themselves on a saved entry, and being told beforehand is the
          difference between that being the feature and it being a surprise.
        */}
        {isNew && (
          <p className="muted">Attaching a receipt will save this entry first.</p>
        )}

        {/*
          No empty state. The drop zone directly below already says "Drop a
          receipt here, or choose a file" -- "Nothing attached yet" above it
          was the same fact stated twice, and it read as a reproach on every
          entry that does not need a receipt. The list appears when there is
          something in it.
        */}

        <ul className="receipt-list">
          {receipts.map((receipt) => (
            <li key={receipt.id}>
              <button
                type="button"
                className="link"
                onClick={() => setViewing(receipt)}
              >
                {receipt.originalFilename}
              </button>
              {/*
                No size and no "fixed -- the entry is posted".
                The size was never something the operator decides anything
                from, and the note repeated, once per receipt, what the banner
                at the top of the page already says once. On an entry with
                three receipts it said it three times.
              */}
              {receipt.deletable && !readOnly && (
                <button
                  type="button"
                  className="link receipt-remove"
                  aria-label={`Remove ${receipt.originalFilename}`}
                  onClick={() => void removeReceipt(receipt)}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>

        {/*
          A drop zone, not a link. A receipt arrives as a file you already have
          -- dragged from a mail client or a downloads folder -- and a text
          link gives that gesture nowhere to land.

          It is a <label>, so the whole rectangle is the file input's own
          affordance: clicking anywhere in it opens the picker, with no click
          handler and nothing to keep in sync with the keyboard. The wording
          covers both gestures because on a phone there is nothing to drag,
          and this was reported from a phone.

          HIDDEN ON A LOCKED ENTRY ONLY. It used to be hidden on every posted
          entry, at the operator's request: a dashed rectangle inviting a drop
          was the largest thing on a page whose every other control had been
          taken away, and it read as an editable record. Since migration 0027 a
          posted entry in an open period IS an editable record, so the zone is
          back there. In a closed period the page is read-only again and the
          same reasoning returns. The Entries pane still files a receipt on any
          entry, which covers the PDF that arrives after a year is closed.
        */}
        {!locked && (
          <ReceiptDropzone
            onResolveTarget={resolveReceiptTarget}
            onAttached={receiptAttached}
            onError={setSaveError}
          />
        )}
      </section>

      <div className="row editor-fields">
        <label>
          Memo{' '}
          <input
            className="memo-input"
            value={memo}
            disabled={locked}
            onChange={(event) => setMemo(event.target.value)}
          />
        </label>
        {/*
          An invoice, a bill, or an entry taking goods out moves no money, so
          it has no cash date (0086, 0087). The field is simply absent there:
          a note saying so stated the obvious (the operator, #65, 2026-10-15).
        */}
        {entry.source === 'manual' && (isDocument || takesGoodsOut) ? null : (
          <label title="§6.4 keys the whole cash-basis conversion off this date">
            Cash Date{' '}
            <input
              type="date"
              value={cashDate}
              // On a bank transaction the cash date is the bank's fact, draft or
              // posted: the service refuses it (bank_fact) and 0055 refuses to
              // post any other (review issue #34). A manual entry's is the
              // operator's own.
              disabled={locked || entry.source !== 'manual'}
              onChange={(event) => setCashDate(event.target.value)}
            />
          </label>
        )}
        {entry.source !== 'manual' && (
          <InfoNote label="Whose Date">
            The cash date of a bank transaction is the date the bank reports it, and it is not edited. Which period the
            books put it in is the entry date, which is yours.
          </InfoNote>
        )}
      </div>

      {/*
        Absent on a locked entry rather than disabled, for the reason the line
        controls are: the notice at the top already says why nothing here can
        change, and greyed buttons beside it invite a click that cannot work.
      */}
      {!locked && (
        <div className="row editor-actions">
          {posted ? (
            <>
              {/* One action. A posted entry stays posted (0027 refuses
                  un-posting), so there is no draft to save it back to. */}
              <button type="button" disabled={busy || !balanced} onClick={() => void save()}>
                Save Changes
              </button>
              {!balanced && (
                <span className="muted">A posted entry has to stay balanced.</span>
              )}
            </>
          ) : (
            <>
              <button type="button" disabled={busy} onClick={() => void save()}>
                Save Draft
              </button>
              <button
                type="button"
                disabled={busy || !balanced}
                onClick={() => void saveAndPost()}
              >
                Save and Post
              </button>
              {!balanced && <span className="muted">Posting needs a balanced entry.</span>}
            </>
          )}
          {deletable && !confirmingDelete && (
            <button
              type="button"
              className="link editor-delete"
              onClick={() => setConfirmingDelete(true)}
            >
              Delete Entry
            </button>
          )}
        </div>
      )}

      {/*
        The delete confirmation, inline, in the shape the Matching screen uses
        for undoing a match. It says what happens to the record rather than
        asking whether the operator is sure: a posted entry leaves its full
        snapshot in History, and a draft leaves nothing.
      */}
      {confirmingDelete && (
        <div className="new-account undo-form" role="group" aria-label="Delete Entry">
          <p>
            {posted
              ? 'Delete this posted entry? It leaves the books, and its History keeps a copy of what it was.'
              : 'Delete this draft? Nothing about it is kept.'}
          </p>
          <div className="row">
            <button type="button" disabled={busy} onClick={() => void deleteEntry()}>
              Delete It
            </button>
            <button type="button" className="link" onClick={() => setConfirmingDelete(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/*
        §9.6 — Reverse, outside the action row on purpose: that row is absent
        when the books are closed through the entry, and a closed period is
        exactly where a reversal is the correction. The confirmation says what
        is created, and asks why, because the server records the reason.
      */}
      {reversible && reversing === null && (
        <p className="editor-reverse">
          <button type="button" className="link" onClick={() => setReversing('')}>
            Reverse Entry
          </button>
          {locked && (
            <span className="muted">
              {' '}
              The books are closed through this entry, so a reversal dated today is how it is
              corrected.
            </span>
          )}
        </p>
      )}
      {reversible && reversing !== null && (
        <div className="new-account undo-form" role="group" aria-label="Reverse Entry">
          <p>
            Reverse this entry? A new posted entry, dated today, swaps every debit and credit so the
            two cancel. This entry stays as it is, and both stay in the books.
          </p>
          <label className="field">
            <span>Why is it being reversed?</span>
            <input
              aria-label="Reason for reversing"
              value={reversing}
              onChange={(event) => setReversing(event.target.value)}
            />
          </label>
          <div className="row">
            <button
              type="button"
              disabled={busy || reversing.trim() === ''}
              onClick={() => void reverseEntry()}
            >
              Reverse It
            </button>
            <button type="button" className="link" onClick={() => setReversing(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/*
        §5.7 — what changed, when, and who did it. Collapsed by default: it is
        read when something looks wrong, not on every open. Offered on a posted
        entry only, because a draft is not recorded (0027) and an empty panel
        on every draft would be a control that never has anything behind it.

        Newest first. The server returns saves oldest first; within one save
        the order of events means nothing, and lib/history.ts lists them in a
        reading order instead.
      */}
      {posted && !isNew && (
        <section className="entry-history" aria-label="History">
          <button
            type="button"
            className="link"
            aria-expanded={historyOpen}
            aria-controls="entry-history"
            onClick={() => setHistoryOpen((open) => !open)}
          >
            History
          </button>
          {historyOpen && (
            <div id="entry-history">
              {/*
                THE ENTRY'S ID, ONCE.

                Asked for so a report can name the row rather than describe it:
                "the Synergycom one from August" cost a session's worth of
                querying by counterparty and amount to identify, and an id is
                one lookup.

                Once per history, not once per change. It identifies the ENTRY,
                and repeating it beside every change would put the same forty
                characters down the page while saying nothing new -- the same
                argument that removed the per-receipt "fixed" note above.

                Only on a saved entry: a new one has no id yet, and `isNew` is
                the honest test rather than rendering the literal "new".
              */}
              {!isNew && (
                <p className="muted history-id">
                  Entry <code>{id}</code>
                </p>
              )}
              {historyError !== null ? (
                <p role="alert" className="error">
                  {historyError}
                </p>
              ) : history === null ? (
                <p className="muted">Loading…</p>
              ) : history.length === 0 ? (
                <p className="muted">No changes recorded.</p>
              ) : (
                <ol className="history-list">
                  {[...history].reverse().map((change) => (
                    <li key={change.events[0]?.id ?? change.at}>
                      <p className="muted history-when">
                        {formatWhen(change.at)} · {change.actorEmail ?? 'No user recorded'}
                      </p>
                      <ul>
                        {describeChange(change, {
                          accountLabel: (accountId) => {
                            const found = allAccounts.find((a) => a.id === accountId);
                            return found ? `${found.code} ${found.name}` : null;
                          },
                          partyName: (candidate) =>
                            parties.find((p) => p.id === candidate)?.name ?? null,
                        }).map((sentence, index) => (
                          <li key={index}>{sentence}</li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}
        </section>
      )}

      {/*
        Keyed on the receipt id for the reason Triage states: switching
        receipts must remount, or the frame keeps the previous document while
        the new signed URL is still in flight.
      */}
      {viewing !== null && (
        <ReceiptOverlay
          key={viewing.id}
          attachment={viewing}
          onClose={() => setViewing(null)}
        />
      )}
    </section>
  );
}
